/**
 * EANCOM Segment Descriptions
 * Based on UN/EDIFACT D96A standards and EANCOM implementation
 * References: 
 * - UN/EDIFACT Syntax Version 4 Release 1
 * - EANCOM (DEDIP D96) documentation
 */

// Segment descriptions with expanded documentation
const segmentDescriptions = {
  // Interchange Header and Service Segments
  'UNA': 'Service String Advice - Defines the special characters used in the interchange',
  'UNB': 'Interchange Header - Identifies the sender and recipient of the interchange',
  'UNG': 'Functional Group Header - Identifies a functional group of messages',
  'UNH': 'Message Header - Identifies a message within a transmission',
  'UNT': 'Message Trailer - End and check of completeness of a message',
  'UNE': 'Functional Group Trailer - End and check of completeness of a functional group',
  'UNZ': 'Interchange Trailer - End and check of completeness of an interchange',
  
  // Business Segments
  'BGM': 'Beginning of Message - Indicates the type and function of a message',
  'DTM': 'Date/Time/Period - Specifies date, time or period information',
  'NAD': 'Name and Address - Identifies a party and its address',
  'RFF': 'Reference - Provides a reference',
  'CTA': 'Contact Information - Identifies a person or department to be contacted',
  'COM': 'Communication Contact - Identifies communication numbers or addresses',
  'CUX': 'Currencies - Specifies currencies and relevant details',
  'PAT': 'Payment Terms Basis - Specifies the terms of payment',
  'TOD': 'Terms of Delivery - Specifies terms of delivery',
  'LOC': 'Place/Location Identification - Identifies a place or location',
  'FTX': 'Free Text - Provides free-form text information',
  'DOC': 'Document/Message Details - Identifies documents referenced',
  'TDT': 'Transport Information - Specifies transport details for the consignment',
  'CPS': 'Consignment Packing Sequence - Identifies packaging hierarchy structure',
  'PAC': 'Package - Describes the number and type of packages',
  'ALI': 'Additional Information - Provides information about country of origin, customs, or special conditions',
  
  // Segments for Line Items
  'LIN': 'Line Item - Identifies a line item in a transaction',
  'PIA': 'Additional Product ID - Provides additional product identification',
  'IMD': 'Item Description - Describes an item',
  'QTY': 'Quantity - Specifies a quantity',
  'MOA': 'Monetary Amount - Specifies a monetary amount',
  'PRI': 'Price Details - Provides price information',
  'TAX': 'Duty/Tax/Fee Details - Specifies tax related information',
  
  // Summary Segments
  'CNT': 'Control Total - Provides control totals',
  'UNS': 'Section Control - Divides a message into sections',
  
  // Specific to INVOIC
  'ALC': 'Allowance or Charge - Specifies allowance or charge information',
  'RCS': 'Requirements and Conditions - Specifies requirements and conditions',
  
  // Specific to ORDERS
  'SCC': 'Scheduling Conditions - Specifies scheduling conditions',
  
  // Specific to DESADV
  'GIN': 'Goods Identity Number - Identifies goods',
  'HAN': 'Handling Instructions - Provides handling instructions',
  'PCI': 'Package Identification - Identifies packages',
  'FII': 'Financial Institution Information - Identifies an account and a related financial institution',
  'PYT': 'Payment Terms - Specifies the payment terms',
  'PCD': 'Percentage Details - Specifies percentage information',
  'MEA': 'Measurements - Specifies physical measurements and dimensions',
  'EQD': 'Equipment Details - Identifies equipment used in transport operations',
  'SEQ': 'Sequence Details - Provides information on sequence and delivery patterns',
  'DGS': 'Dangerous Goods - Identifies dangerous goods and provides handling information',
  'STS': 'Status - Specifies the status of an object or service',
  'AGR': 'Agreement Identification - Specifies a contract or agreement details',
  
  // Financial Segments
};

// Extended explanations for segments with detailed function and usage
const segmentExtendedInfo = {
  'UNA': {
    title: 'Service String Advice',
    function: 'To define the special characters used in the transmission. This segment specifies the delimiters for the transmission.',
    elements: [
      { id: '1', name: 'COMPONENT DATA ELEMENT SEPARATOR', mandatory: true, description: 'Default value is : (colon)' },
      { id: '2', name: 'DATA ELEMENT SEPARATOR', mandatory: true, description: 'Default value is + (plus sign)' },
      { id: '3', name: 'DECIMAL NOTATION', mandatory: true, description: 'Default value is . (period)' },
      { id: '4', name: 'RELEASE CHARACTER', mandatory: true, description: 'Default value is ? (question mark)' },
      { id: '5', name: 'RESERVED', mandatory: true, description: 'Default value is space' },
      { id: '6', name: 'SEGMENT TERMINATOR', mandatory: true, description: 'Default value is \' (apostrophe)' }
    ],
    notes: 'If used, the UNA segment must appear at the beginning of the interchange, before the UNB segment. If not transmitted, the default values for delimiters apply.'
  },
  'UNB': {
    title: 'Interchange Header',
    function: 'To identify an interchange. This segment starts each interchange and contains addressing and control information.',
    elements: [
      { id: 'S001', name: 'SYNTAX IDENTIFIER', mandatory: true, description: 'Identifies the syntax rules and controlling agency', examples: [
        'UNOC:3', 'UNOA:3', 'UNOB:3'
      ] },
      { id: 'S002', name: 'INTERCHANGE SENDER', mandatory: true, description: 'Identifies the sender of the interchange', subElements: [
        { id: '0004', name: 'Sender identification', description: 'EAN/UCC location number or other sender identifier' },
        { id: '0007', name: 'Identification code qualifier', description: 'Qualifier defining the type of ID (e.g., "14" for EAN)' },
        { id: '0008', name: 'Address for reverse routing', description: 'Additional identification for routing back' }
      ] },
      { id: 'S003', name: 'INTERCHANGE RECIPIENT', mandatory: true, description: 'Identifies the recipient of the interchange', subElements: [
        { id: '0010', name: 'Recipient identification', description: 'EAN/UCC location number or other recipient identifier' },
        { id: '0007', name: 'Identification code qualifier', description: 'Qualifier defining the type of ID (e.g., "14" for EAN)' },
        { id: '0014', name: 'Routing address', description: 'Additional identification for routing' }
      ] },
      { id: 'S004', name: 'DATE AND TIME OF PREPARATION', mandatory: true, description: 'Date and time when the interchange was prepared', subElements: [
        { id: '0017', name: 'Date', description: 'Preparation date in format YYMMDD' },
        { id: '0019', name: 'Time', description: 'Preparation time in format HHMM' }
      ] },
      { id: '0020', name: 'INTERCHANGE CONTROL REFERENCE', mandatory: true, description: 'Unique reference assigned by the sender' },
      { id: 'S005', name: 'RECIPIENT REFERENCE/PASSWORD', mandatory: false, description: 'Recipient\'s password identification', subElements: [
        { id: '0022', name: 'Recipient reference/password', description: 'Password or reference value' },
        { id: '0025', name: 'Recipient reference/password qualifier', description: 'Qualifier for the password or reference' }
      ] },
      { id: '0026', name: 'APPLICATION REFERENCE', mandatory: false, description: 'Reference to the application', examples: ['EANCOM', 'ORDERS'] },
      { id: '0029', name: 'PROCESSING PRIORITY CODE', mandatory: false, description: 'Processing priority code', examples: ['A', 'B', 'C'] },
      { id: '0031', name: 'ACKNOWLEDGEMENT REQUEST', mandatory: false, description: 'Request for acknowledgement', examples: ['1'] },
      { id: '0032', name: 'COMMUNICATIONS AGREEMENT ID', mandatory: false, description: 'Identification of the agreement', examples: ['EANCOM'] },
      { id: '0035', name: 'TEST INDICATOR', mandatory: false, description: 'Indicates if this is a test', examples: ['1'] }
    ],
    notes: 'The UNB segment is the first segment in an EDIFACT interchange and identifies the sender and recipient of the message. The combination of the values in S002, S003, and 0020 uniquely identifies the interchange for acknowledgement purposes. In EANCOM, the syntax identifier is typically UNOC:3 for full character set, with identification code qualifier "14" for EAN location coding. The test indicator value "1" indicates a test message.',
    example: 'UNB+UNOC:3+7080003000007:14+7080005000008:14+200207:0830+1001++ORDERS++1+EANCOM+1'
  },
  'UNH': {
    title: 'Message Header',
    function: 'To head, identify and specify a message.',
    elements: [
      { id: '0062', name: 'MESSAGE REFERENCE NUMBER', mandatory: true, description: 'Unique message reference assigned by the sender' },
      { id: 'S009', name: 'MESSAGE IDENTIFIER', mandatory: true, description: 'Identification of the message type, version, etc.', subElements: [
        { id: '0065', name: 'Message type', description: 'Type of message (e.g., ORDERS, INVOIC, DESADV)', examples: ['ORDERS', 'INVOIC', 'DESADV'] },
        { id: '0052', name: 'Message version number', description: 'Version number of the message type', examples: ['D'] },
        { id: '0054', name: 'Message release number', description: 'Release number within the message version', examples: ['96A', '01B'] },
        { id: '0051', name: 'Controlling agency', description: 'Agency controlling the message specification', examples: ['UN'] },
        { id: '0057', name: 'Association assigned code', description: 'Code assigned by the association', examples: ['EAN003', 'EAN007'] }
      ] },
      { id: '0068', name: 'COMMON ACCESS REFERENCE', mandatory: false, description: 'Reference serving as a key to relate all messages of a transaction' },
      { id: 'S010', name: 'STATUS OF THE TRANSFER', mandatory: false, description: 'Indication of message status', subElements: [
        { id: '0070', name: 'Sequence of transfers', description: 'Number in a sequence of message transfers' },
        { id: '0073', name: 'First and last transfer', description: 'Indication of first/last message in a sequence' }
      ] }
    ],
    notes: 'The UNH segment is required at the start of each message. In EANCOM, the message type identifier identifies the specific business message type, version, and release. The UNH message reference number must be unique within the interchange and is referenced in the UNT segment. The common access reference allows multiple messages to be linked within a business transaction.',
    example: 'UNH+ME000001+ORDERS:D:96A:UN:EAN007'
  },
  'BGM': {
    title: 'Beginning of Message',
    function: 'To indicate the type and function of a message and to transmit the identifying number.',
    elements: [
      { id: 'C002', name: 'DOCUMENT/MESSAGE NAME', mandatory: false, description: 'Code or name identifying the document', subElements: [
        { id: '1001', name: 'Document/message name, coded', description: 'Code identifying the document/message', examples: ['220', '351', '380'] },
        { id: '1131', name: 'Code list qualifier', description: 'Identification of the code list' },
        { id: '3055', name: 'Code list responsible agency, coded', description: 'Code identifying the agency responsible for the code list' },
        { id: '1000', name: 'Document/message name', description: 'Plain language name of the document' }
      ] },
      { id: 'C106', name: 'DOCUMENT/MESSAGE IDENTIFICATION', mandatory: false, description: 'Document number/identifier', subElements: [
        { id: '1004', name: 'Document/message number', description: 'Reference number of the document/message' },
        { id: '1056', name: 'Version', description: 'Version of the document/message' },
        { id: '1060', name: 'Revision number', description: 'Revision number of the document/message' }
      ] },
      { id: '1225', name: 'MESSAGE FUNCTION, CODED', mandatory: false, description: 'Code indicating the function of the message', examples: ['9', '5', '7'] },
      { id: '4343', name: 'RESPONSE TYPE, CODED', mandatory: false, description: 'Code specifying the type of acknowledgment required' }
    ],
    qualifiers: {
      '9': 'Purchase order',
      '105': 'Purchase order change request',
      '220': 'Order',
      '231': 'Purchase order response',
      '240': 'Delivery instruction',
      '241': 'Delivery order',
      '242': 'Delivery state',
      '244': 'Delivery release',
      '310': 'Offer/quotation',
      '351': 'Dispatch advice',
      '380': 'Commercial invoice',
      '481': 'Remittance advice'
    },
    functionCodes: {
      '1': 'Cancellation',
      '2': 'Addition',
      '3': 'Deletion',
      '4': 'Change',
      '5': 'Replace',
      '6': 'Confirmation',
      '7': 'Duplicate',
      '8': 'Status',
      '9': 'Original',
      '10': 'Not found',
      '11': 'Response',
      '12': 'Not processed',
      '13': 'Request',
      '14': 'Advance notification',
      '15': 'Reminder',
      '16': 'Proposal',
      '17': 'Cancel, to be reissued',
      '18': 'Reissue',
      '19': 'Seller initiated change',
      '20': 'Replace heading section only',
      '21': 'Replace item detail and summary only',
      '22': 'Final transmission',
      '23': 'Transaction on hold',
      '24': 'Delivery instruction',
      '25': 'Forecast',
      '26': 'Delivery instruction and forecast',
      '27': 'Not accepted',
      '28': 'Accepted, with amendment in heading section',
      '29': 'Accepted without amendment',
      '30': 'Accepted, with amendment in detail section',
      '31': 'Copy',
      '32': 'Approval',
      '33': 'Change in heading section',
      '34': 'Accepted with amendment',
      '35': 'Retransmission',
      '36': 'Change in detail section',
      '37': 'Reversal of a debit',
      '38': 'Reversal of a credit',
      '39': 'Reversal for cancellation',
      '40': 'Request for deletion',
      '41': 'Finishing/closing order',
      '42': 'Confirmation via specific means',
      '43': 'Additional transmission',
      '44': 'Accepted without reserves',
      '45': 'Accepted with reserves',
      '46': 'Provisional',
      '47': 'Definitive',
      '48': 'Accepted, contents rejected',
      '49': 'Settled dispute',
      '50': 'Withdraw',
      '51': 'Authorisation',
      '52': 'Proposed amendment',
      '53': 'Test'
    },
    responseTypeCodes: {
      '1': 'Required',
      '2': 'Not required',
      '3': 'Preferred',
      'AA': 'Acknowledge with detail and no change',
      'AB': 'Acknowledge only',
      'AC': 'Acknowledge with detail and change',
      'AI': 'Acknowledge only if exception',
      'NA': 'No acknowledgement needed'
    },
    notes: 'The BGM segment is used at the beginning of the message to indicate the message type, its function, and to transmit its identifying number. In EANCOM, the document/message name code identifies the business document type (e.g., 220 for Order, 351 for Dispatch Advice). The document number should be a unique reference that will be used for future reference to this specific business document. The message function code indicates if this is an original, replacement, or other type of message.',
    example: 'BGM+220+1234567+9'
  },
  'DTM': {
    title: 'Date/Time/Period',
    function: 'To specify date, time or period information related to the message.',
    elements: [
      { id: 'C507', name: 'DATE/TIME/PERIOD', mandatory: true, description: 'Date/time/period in a specified format', subElements: [
        { id: '2005', name: 'Date/time/period qualifier', description: 'Code qualifying the date/time/period', examples: ['137', '3', '4', '11'] },
        { id: '2380', name: 'Date/time/period', description: 'The actual date, time, or period value', examples: ['20200120', '202001', '2020'] },
        { id: '2379', name: 'Date/time/period format qualifier', description: 'Code specifying the format of the date/time/period', examples: ['102', '203', '101'] }
      ] }
    ],
    qualifiers: {
      '2': 'Delivery date/time, requested',
      '3': 'Invoice date/time',
      '4': 'Order date/time',
      '7': 'Effective date/time',
      '10': 'Shipment date/time, requested',
      '11': 'Dispatch date and/or time',
      '12': 'Terms discount due date/time',
      '13': 'Terms discount due date/time',
      '17': 'Delivery date/time, estimated',
      '35': 'Delivery date/time, actual',
      '36': 'Expiry date',
      '50': 'Goods receipt date/time',
      '63': 'Delivery date/time, latest',
      '64': 'Delivery date/time, earliest',
      '67': 'Delivery date/time, current schedule',
      '69': 'Delivery date/time, promised for',
      '71': 'Delivery date/time, requested for (after and including)',
      '72': 'Delivery date/time, requested for (before and including)',
      '76': 'Production/manufacture date',
      '137': 'Document/message date/time',
      '140': 'Payment due date',
      '167': 'Charge period start date',
      '168': 'Charge period end date',
      '169': 'Lead time',
      '171': 'Reference date/time',
      '194': 'Start date/time',
      '200': 'Pick-up/collection date/time',
      '206': 'End date/time',
      '263': 'Invoicing period',
      '325': 'Tax period'
    },
    formats: {
      '101': 'YYMMDD (Year, Month, Day)',
      '102': 'CCYYMMDD (Century, Year, Month, Day)',
      '103': 'YYWW (Year, Week)',
      '104': 'MMWW (Month, Week)',
      '201': 'YYMMDDHHMM (Year, Month, Day, Hour, Minute)',
      '203': 'CCYYMMDDHHMM (Century, Year, Month, Day, Hour, Minute)',
      '204': 'CCYYMMDDHHMMSS (Century, Year, Month, Day, Hour, Minute, Second)',
      '401': 'HHMM (Hour, Minute)',
      '501': 'HHMMHHMM (From Hour Minute To Hour Minute)'
    },
    notes: 'The DTM segment specifies dates and times related to the transaction. For EANCOM messages, the format qualifier is important and typically uses code 102 (CCYYMMDD) for dates. The most common use of DTM is with qualifier 137 for document date, qualifier 4 for order date, qualifier 11 for dispatch date, and qualifier 200 for pick-up/collection date/time.',
    examples: [
      'DTM+137:20200615:102',
      'DTM+200:20250429:102'
    ]
  },
  'NAD': {
    title: 'Name and Address',
    function: 'To specify the name/address and their related function, relevant to the message.',
    elements: [
      { id: '3035', name: 'PARTY QUALIFIER', mandatory: true, description: 'Code identifying the party', examples: ['BY', 'SE', 'ST', 'SU'] },
      { id: 'C082', name: 'PARTY IDENTIFICATION DETAILS', mandatory: false, description: 'Identification of a transaction party by code', subElements: [
        { id: '3039', name: 'Party id. identification', description: 'Identification of a party by code', examples: ['5412345000176', '5790000456235'] },
        { id: '1131', name: 'Code list qualifier', description: 'Identification of a code list', examples: ['9'] },
        { id: '3055', name: 'Code list responsible agency, coded', description: 'Code identifying the agency responsible for a code list', examples: ['9', '91', '92'] }
      ] },
      { id: 'C058', name: 'NAME AND ADDRESS', mandatory: false, description: 'Name and address in clear form', subElements: [
        { id: '3124', name: 'Name and address line', description: 'Free form name and address line', examples: ['Main Street 123', 'Building A'] }
      ] },
      { id: 'C080', name: 'PARTY NAME', mandatory: false, description: 'Name of a party', subElements: [
        { id: '3036', name: 'Party name', description: 'Name of a party', examples: ['ABC Company Ltd', 'XYZ Corporation'] },
        { id: '3036', name: 'Party name', description: 'Name of a party (continued)', examples: ['Electronic Division', 'IT Department'] }
      ] },
      { id: 'C059', name: 'STREET', mandatory: false, description: 'Street address and/or P.O. Box', subElements: [
        { id: '3042', name: 'Street and number/P.O. Box', description: 'Street and number in plain language', examples: ['123 Main Street', 'P.O. Box 5678'] }
      ] },
      { id: '3164', name: 'CITY NAME', mandatory: false, description: 'Name of city', examples: ['Brussels', 'Paris', 'London'] },
      { id: '3229', name: 'COUNTRY SUB-ENTITY IDENTIFICATION', mandatory: false, description: 'To identify a country subdivision', examples: ['CA', 'NY', 'ON'] },
      { id: '3251', name: 'POSTAL CODE', mandatory: false, description: 'Postal code', examples: ['1000', '75001', 'SW1A 1AA'] },
      { id: '3207', name: 'COUNTRY, CODE', mandatory: false, description: 'ISO country code', examples: ['BE', 'FR', 'GB'] }
    ],
    qualifiers: {
      'BY': 'Buyer',
      'SE': 'Seller',
      'ST': 'Ship to',
      'SU': 'Supplier',
      'CN': 'Consignee',
      'DP': 'Delivery party',
      'IV': 'Invoicee',
      'PE': 'Payee',
      'UC': 'Ultimate consignee',
      'II': 'Issuer of invoice',
      'UD': 'Ultimate customer'
    },
    idQualifiers: {
      '9': 'EAN (EAN International)',
      '14': 'EAN Location Code (GLN)',
      '91': 'Assigned by seller or seller\'s agent',
      '92': 'Assigned by buyer or buyer\'s agent'
    },
    notes: 'The NAD segment identifies a party and may provide its address. In EANCOM, parties are typically identified by their EAN/GLN (Global Location Number) in element 3039 with code list responsible agency code "9" (EAN). A single NAD segment with qualifier "BY" (buyer) is mandatory in ORDERS messages, while both "BY" and "SU" (supplier) are mandatory in INVOIC messages.',
    example: 'NAD+BY+5412345000176::9'
  },
  'LIN': {
    title: 'Line Item',
    function: 'To identify a line item and configuration.',
    elements: [
      { id: '1082', name: 'LINE ITEM NUMBER', mandatory: false, description: 'Sequential number of the line item within the document', examples: ['1', '2', '10'] },
      { id: '1229', name: 'ACTION REQUEST/NOTIFICATION, CODED', mandatory: false, description: 'Code specifying the action to be taken or status', examples: ['1', '3', '5'] },
      { id: 'C212', name: 'ITEM NUMBER IDENTIFICATION', mandatory: false, description: 'Identification of an item by code', subElements: [
        { id: '7140', name: 'Item number', description: 'Code identifying an item', examples: ['5412345123456', '4000862141404'] },
        { id: '7143', name: 'Item number type, coded', description: 'Identifying the type of item number', examples: ['SRV', 'EN'] },
        { id: '1131', name: 'Code list qualifier', description: 'Qualifier for the coded data element', examples: ['9'] },
        { id: '3055', name: 'Code list responsible agency, coded', description: 'Code identifying the agency responsible for the code list', examples: ['9'] }
      ] }
    ],
    actionCodes: {
      '1': 'Added item',
      '2': 'Deleted item',
      '3': 'Changed item',
      '4': 'Item accepted',
      '5': 'Item rejected',
      '6': 'Confirmation of item',
      '7': 'Item accepted with amendment'
    },
    itemNumberTypes: {
      'SRV': 'GS1 Global Trade Item Number (GTIN)',
      'EN': 'International Article Number (EAN)',
      'UP': 'UPC (Universal Product Code)',
      'MF': 'Manufacturer\'s part number',
      'BP': 'Buyer\'s part number',
      'IN': 'Buyer\'s item number',
      'SA': 'Supplier\'s article number'
    },
    notes: 'The LIN segment is used to identify each item being ordered, delivered, or invoiced. In EANCOM, items are typically identified by their GTIN (Global Trade Item Number) or EAN using item number type code "SRV" or "EN" and agency code "9" for EAN/GS1. Each line item usually has a sequence number and may appear with an action code indicating what to do with this line item.',
    example: 'LIN+1++5412345123456:SRV'
  },
  'QTY': {
    title: 'Quantity',
    function: 'To specify a quantity.',
    elements: [
      { id: 'C186', name: 'QUANTITY DETAILS', mandatory: true, description: 'Quantity information with code, amount, and unit' }
    ],
    qualifiers: {
      '1': 'Discrete quantity',
      '11': 'Split quantity',
      '12': 'Despatch quantity',
      '21': 'Ordered quantity',
      '46': 'Delivered quantity',
      '47': 'Invoiced quantity',
      '52': 'Quantity per pack',
      '59': 'Number of consumer units in a trading unit',
      '61': 'Return quantity',
      '83': 'Backorder quantity',
      '113': 'Quantity shipped, to date',
      '192': 'Free goods quantity'
    }
  },
  'MOA': {
    title: 'Monetary Amount',
    function: 'To specify a monetary amount with its qualifier.',
    elements: [
      { id: 'C516', name: 'MONETARY AMOUNT', mandatory: true, description: 'Monetary amount with qualifier, value, and optional currency', subElements: [
        { id: '5025', name: 'Monetary amount type qualifier', description: 'Code qualifying the type of monetary amount', mandatory: true, examples: ['9', '22', '77', '79', '86', '124', '125'] },
        { id: '5004', name: 'Monetary amount', description: 'The actual monetary amount value', mandatory: false, examples: ['1250.00', '325.50', '198.75'] },
        { id: '6345', name: 'Currency, coded', description: 'Code for the currency of the monetary amount', mandatory: false, examples: ['EUR', 'USD', 'GBP'] },
        { id: '6343', name: 'Currency qualifier', description: 'Code qualifying the currency', mandatory: false, examples: ['10', '11'] },
        { id: '4405', name: 'Status, coded', description: 'Code specifying the status of the amount', mandatory: false, examples: ['3', '7'] }
      ] }
    ],
    qualifiers: {
      '9': 'Amount due/amount payable',
      '22': 'Cash discount amount',
      '77': 'Invoice amount',
      '79': 'Total line items amount',
      '86': 'Message total monetary amount',
      '118': 'Taxable amount',
      '124': 'Tax amount',
      '125': 'Taxable amount - excluding tax',
      '128': 'Total amount',
      '131': 'Total charges/allowances',
      '139': 'Total payment discount',
      '150': 'Allowance/charge amount',
      '176': 'Message total duty/tax/fee amount',
      '203': 'Total amount subject to payment discount',
      '260': 'Total value of expected payments',
      '304': 'Order total',
      '5': 'Adjusted amount',
      '8': 'Applicable amount',
      '12': 'Allowance amount',
      '15': 'Amount of lower value equivalent',
      '20': 'Charge amount',
      '21': 'Charge or allowance amount',
      '25': 'Charge/allowance amount',
      '52': 'Discount amount',
      '54': 'Discount amount due to incident',
      '57': 'Equivalent amount',
      '66': 'Customs value amount',
      '166': 'Tax basis amount',
      '369': 'Goods and services tax amount',
      '388': 'Total amount including Value Added Tax (VAT)',
      'NET': 'Net amount'
    },
    notes: 'The MOA segment specifies monetary amounts relevant to the transaction. In EANCOM, MOA is used in various sections of messages to indicate different types of monetary amounts. MOA+86 is often used for the total message amount, MOA+79 for line item totals, and MOA+124 for tax amounts. If the currency is not specified in the MOA segment, it is taken from the document currency specified in the CUX segment.\n\nFormat: MOA+qualifier:amount[:currency] - Example: MOA+86:1250.00:EUR',
    examples: [
      'MOA+86:1250.00 - Total monetary amount of the message is 1250.00 in the document currency',
      'MOA+79:520.75 - Total line items amount is 520.75',
      'MOA+124:250.00 - Tax amount is 250.00',
      'MOA+9:1520.75:EUR - Amount due is 1520.75 EUR'
    ]
  },
  'PRI': {
    title: 'Price Details',
    function: 'To specify price information.',
    elements: [
      { id: 'C509', name: 'PRICE INFORMATION', mandatory: false, description: 'Price information with qualifier and amount' },
      { id: '5213', name: 'SUB-LINE PRICE CHANGE, CODED', mandatory: false, description: 'Code specifying the type of price change' }
    ],
    qualifiers: {
      'AAA': 'Net calculation',
      'AAB': 'Gross calculation',
      'AAE': 'Information price, excluding allowances/charges',
      'INF': 'Information',
      'INV': 'Invoice price',
      'NTP': 'Net unit price',
      'PRP': 'Promotional price'
    }
  },
  'RFF': {
    title: 'Reference',
    function: 'To specify a reference.',
    elements: [
      { id: 'C506', name: 'REFERENCE', mandatory: true, description: 'Reference information with qualifier and number', subElements: [
        { id: '1153', name: 'Reference qualifier', description: 'Code qualifying the reference', examples: ['ON', 'CT', 'IV'] },
        { id: '1154', name: 'Reference number', description: 'The actual reference number', examples: ['PO12345', 'INV98765', 'CONT2020'] },
        { id: '1156', name: 'Line number', description: 'Line number within a reference', examples: ['1', '2'] },
        { id: '4000', name: 'Reference version number', description: 'Version number of the reference', examples: ['1', '001'] }
      ] }
    ],
    qualifiers: {
      'ON': 'Order number',
      'VN': 'Order number (supplier)',
      'CT': 'Contract number',
      'PO': 'Purchase order number',
      'IV': 'Invoice number',
      'ADU': 'Despatch unit',
      'VA': 'VAT registration number',
      'CR': 'Customer reference number',
      'AAK': 'Despatch advice number',
      'AAS': 'Transport document number',
      'AID': 'Certificate of conformity',
      'DL': 'Debit note number',
      'CLI': 'Client number',
      'AAJ': 'Delivery order number',
      'PQ': 'Payment reference',
      'AAB': 'Proforma invoice number'
    },
    notes: 'The RFF segment provides references relevant to the business transaction. In EANCOM, the most common references are order number (ON), invoice number (IV), and delivery note number (AAJ). When the RFF segment appears in the heading section of a message, the reference applies to the whole message. When it appears in a line item section, it applies only to that line item.',
    example: 'RFF+ON:PO12345'
  },
  'ALI': {
    title: 'Additional Information',
    function: 'To provide additional information about the preceding item.',
    description: 'To specify country of origin, customs procedures, and other handling information',
    elements: [
      { id: '3239', name: 'COUNTRY OF ORIGIN, CODED', mandatory: false, description: 'ISO country code of the country where goods originate' },
      { id: '9213', name: 'TYPE OF DUTY REGIME, CODED', mandatory: false, description: 'Code specifying the duty or tax treatment' },
      { id: '4183', name: 'SPECIAL CONDITIONS, CODED', mandatory: false, description: 'Code identifying special conditions' },
      { id: '4183', name: 'SPECIAL CONDITIONS, CODED', mandatory: false, description: 'Additional special conditions code' },
      { id: '4183', name: 'SPECIAL CONDITIONS, CODED', mandatory: false, description: 'Additional special conditions code' }
    ],
    qualifiers: {
      'NO': 'Not processed',
      'FE': 'Free circulation'
    },
    notes: 'The ALI segment provides additional information about items, typically country of origin and special handling requirements. It is commonly used in DESADV messages to specify origin country.',
    example: 'ALI+NO'
  },
  'CTA': {
    title: 'Contact Information',
    function: 'To identify a person or department to whom communication should be directed.',
    elements: [
      { id: '3139', name: 'CONTACT FUNCTION, CODED', mandatory: false, description: 'Code identifying the function of the contact' },
      { id: 'C056', name: 'DEPARTMENT OR EMPLOYEE DETAILS', mandatory: false, description: 'Contact department or name' }
    ],
    qualifiers: {
      'AD': 'Accounting',
      'AP': 'Accounts payable',
      'AR': 'Accounts receivable',
      'CA': 'Carrier',
      'CT': 'Contract contact',
      'DL': 'Delivery contact',
      'ED': 'Engineering department',
      'IC': 'Information contact',
      'LO': 'Logistics contact',
      'OC': 'Order contact',
      'QC': 'Quality coordinator',
      'SA': 'Sales administration',
      'SD': 'Shipping department',
      'TR': 'Transport contact',
      'PD': 'Purchasing department'
    }
  },
  'CUX': {
    title: 'Currencies',
    function: 'To specify currencies used in the transaction and relevant details for the rate of exchange.',
    elements: [
      { id: 'C504', name: 'CURRENCY DETAILS', mandatory: false, description: 'Currency details and qualifier' },
      { id: 'C504', name: 'CURRENCY DETAILS', mandatory: false, description: 'Second currency details when conversion is required' },
      { id: '5402', name: 'RATE OF EXCHANGE', mandatory: false, description: 'Rate of exchange between currencies' },
      { id: '6341', name: 'CURRENCY MARKET EXCHANGE, CODED', mandatory: false, description: 'Code identifying the market for currency exchange' }
    ],
    qualifiers: {
      '2': 'Reference currency',
      '3': 'Target currency',
      '5': 'Accounting currency',
      '9': 'Order currency',
      '10': 'Pricing currency',
      '11': 'Payment currency'
    }
  },
  'PIA': {
    title: 'Additional Product ID',
    function: 'To specify additional or alternative product identification.',
    elements: [
      { id: '4347', name: 'PRODUCT ID FUNCTION QUALIFIER', mandatory: true, description: 'Code giving specific meaning to the product ID' },
      { id: 'C212', name: 'ITEM NUMBER IDENTIFICATION', mandatory: true, description: 'Item identification' },
      { id: 'C212', name: 'ITEM NUMBER IDENTIFICATION', mandatory: false, description: 'Additional item identification' },
      { id: 'C212', name: 'ITEM NUMBER IDENTIFICATION', mandatory: false, description: 'Additional item identification' },
      { id: 'C212', name: 'ITEM NUMBER IDENTIFICATION', mandatory: false, description: 'Additional item identification' },
      { id: 'C212', name: 'ITEM NUMBER IDENTIFICATION', mandatory: false, description: 'Additional item identification' }
    ],
    qualifiers: {
      '1': 'Additional identification',
      '2': 'Product identification',
      '3': 'Supplier\'s identification',
      '4': 'Shipment identification',
      '5': 'Product group'
    }
  },
  'IMD': {
    title: 'Item Description',
    function: 'To describe an item in plain language and/or coded format.',
    elements: [
      { id: '7077', name: 'ITEM DESCRIPTION TYPE, CODED', mandatory: false, description: 'Code indicating the format of the description' },
      { id: '7081', name: 'ITEM CHARACTERISTIC, CODED', mandatory: false, description: 'Code specifying the characteristic of the item' },
      { id: 'C273', name: 'ITEM DESCRIPTION', mandatory: false, description: 'Description of the item in text and/or coded form' }
    ],
    qualifiers: {
      'A': 'Added description',
      'C': 'Changed description',
      'D': 'Deleted description',
      'F': 'Free-form description'
    },
    characteristicCodes: {
      'AC': 'Additional identification',
      'BS': 'Brand name',
      'IN': 'Insurance',
      'MD': 'Method of delivery',
      'MN': 'Model number',
      'PC': 'Product code',
      'PO': 'Process',
      'SN': 'Serial number',
      'TF': 'Technical features'
    }
  },
  'UNS': {
    title: 'Section Control',
    function: 'To separate header, detail and summary sections of a message.',
    elements: [
      { id: '0081', name: 'SECTION IDENTIFICATION', mandatory: true, description: 'Code to identify the section of the message' }
    ],
    qualifiers: {
      'D': 'Detail section',
      'H': 'Header section',
      'S': 'Summary section'
    },
    notes: 'This segment is used to separate the header, detail, and summary sections of a message. It is mandatory in some message types like INVOIC, ORDERS, and DESADV.'
  },
  'CNT': {
    title: 'Control Total',
    function: 'To provide control total for checking purposes.',
    elements: [
      { id: 'C270', name: 'CONTROL', mandatory: true, description: 'Control totals with qualifier and value' }
    ],
    qualifiers: {
      '1': 'Total number of line items',
      '2': 'Total number of messages',
      '3': 'Control count',
      '11': 'Total number of packages',
      '15': 'Total consignment quantity',
      '16': 'Total number of sequence details',
      '1E': 'Number of units',
      '2E': 'Number of segments'
    }
  },
  'UNT': {
    title: 'Message Trailer',
    function: 'To end and check the completeness of a message.',
    elements: [
      { id: '0074', name: 'NUMBER OF SEGMENTS IN A MESSAGE', mandatory: true, description: 'Count of the number of segments in the message' },
      { id: '0062', name: 'MESSAGE REFERENCE NUMBER', mandatory: true, description: 'Unique message reference number as specified in UNH' }
    ],
    notes: 'This segment is used to end the message and provides a check on the completeness of the message. The message reference in UNT must match the one specified in UNH.'
  },
  'UNZ': {
    title: 'Interchange Trailer',
    function: 'To end and check the completeness of an interchange.',
    elements: [
      { id: '0036', name: 'INTERCHANGE CONTROL COUNT', mandatory: true, description: 'Count of the number of messages or functional groups in the interchange' },
      { id: '0020', name: 'INTERCHANGE CONTROL REFERENCE', mandatory: true, description: 'Interchange reference number as specified in UNB' }
    ],
    notes: 'This segment is used to end the interchange and provides a check on the completeness of the transmission. The control reference in UNZ must match the one specified in UNB.'
  },
  'UNG': {
    title: 'Functional Group Header',
    function: 'To head, identify and specify a functional group of messages.',
    elements: [
      { id: '0038', name: 'FUNCTIONAL GROUP IDENTIFICATION', mandatory: true, description: 'Identification of the functional group type' },
      { id: 'S006', name: 'APPLICATION SENDER IDENTIFICATION', mandatory: true, description: 'Identification of the application sender' },
      { id: 'S007', name: 'APPLICATION RECIPIENT IDENTIFICATION', mandatory: true, description: 'Identification of the application recipient' },
      { id: 'S004', name: 'DATE AND TIME OF PREPARATION', mandatory: true, description: 'Date and time of preparation of the functional group' },
      { id: '0048', name: 'FUNCTIONAL GROUP REFERENCE NUMBER', mandatory: true, description: 'Unique reference assigned by the sender to identify the functional group' },
      { id: '0051', name: 'CONTROLLING AGENCY', mandatory: true, description: 'Code identifying the agency controlling the specification, maintenance and publication' },
      { id: 'S008', name: 'MESSAGE VERSION', mandatory: false, description: 'Version, release, and control agency of the messages in the functional group' },
      { id: '0058', name: 'APPLICATION PASSWORD', mandatory: false, description: 'Password to the recipient\'s system or to a third party network' }
    ],
    notes: 'This segment is used to head, identify and specify a functional group of messages. It is not mandatory in EANCOM but may be used to group similar message types together.'
  },
  'UNE': {
    title: 'Functional Group Trailer',
    function: 'To end and check the completeness of a functional group.',
    elements: [
      { id: '0060', name: 'NUMBER OF MESSAGES', mandatory: true, description: 'Control count of the number of messages within the functional group' },
      { id: '0048', name: 'FUNCTIONAL GROUP REFERENCE NUMBER', mandatory: true, description: 'Reference number assigned by the sender to the functional group' }
    ],
    notes: 'This segment is used to terminate a functional group and provide a control count of the number of messages in the group. The reference number in UNE must match the one specified in UNG.'
  },
  'COM': {
    title: 'Communication Contact',
    function: 'To identify a communication number of a department or a person to whom communication should be directed.',
    elements: [
      { id: 'C076', name: 'COMMUNICATION CONTACT', mandatory: true, description: 'Communication number with qualifier specifying the type of communication' }
    ],
    qualifiers: {
      'EM': 'Electronic mail',
      'FX': 'Fax',
      'TE': 'Telephone',
      'TL': 'Telex',
      'XF': 'X.400',
      'IC': 'Information contact number',
      'TG': 'Telegraph',
      'AO': 'Uniform Resource Location (URL)',
      'EI': 'EDI transmission',
      'MA': 'Mail',
      'PL': 'Mobile phone'
    },
    notes: 'This segment is commonly used in conjunction with a CTA (Contact Information) segment to provide the actual communication number for the specified contact.'
  },
  'TAX': {
    title: 'Duty/Tax/Fee Details',
    function: 'To specify tax related information such as type, category, rate, and base amount.',
    elements: [
      { 
        id: '5283', 
        name: 'DUTY/TAX/FEE FUNCTION QUALIFIER', 
        mandatory: true, 
        description: 'Code qualifying the function of the duty/tax/fee',
        examples: ['7']
      },
      { 
        id: 'C241', 
        name: 'DUTY/TAX/FEE TYPE', 
        mandatory: false, 
        description: 'Code and/or name identifying the type of duty, tax or fee',
        subElements: [
          { id: '5153', name: 'Duty/tax/fee type, coded', description: 'Code identifying the type of duty/tax/fee' },
          { id: '1131', name: 'Code list qualifier', description: 'Identification of a code list' },
          { id: '3055', name: 'Code list responsible agency, coded', description: 'Code identifying the agency responsible for a code list' },
          { id: '5152', name: 'Duty/tax/fee type', description: 'Name of the duty/tax/fee type' }
        ],
        examples: ['VAT']
      },
      { 
        id: 'C533', 
        name: 'DUTY/TAX/FEE ACCOUNT DETAIL', 
        mandatory: false, 
        description: 'Details regarding the duty, tax or fee account',
        subElements: [
          { id: '5289', name: 'Duty/tax/fee account identification', description: 'Identification of the duty/tax/fee account' },
          { id: '1131', name: 'Code list qualifier', description: 'Identification of a code list' },
          { id: '3055', name: 'Code list responsible agency, coded', description: 'Code identifying the agency responsible for a code list' }
        ]
      },
      { 
        id: '5286', 
        name: 'DUTY/TAX/FEE ASSESSMENT BASIS', 
        mandatory: false, 
        description: 'Value on which a duty, tax or fee assessment is based'
      },
      { 
        id: 'C243', 
        name: 'DUTY/TAX/FEE DETAIL', 
        mandatory: false, 
        description: 'Rate and/or code of the duty, tax or fee',
        subElements: [
          { id: '5279', name: 'Duty/tax/fee rate identification', description: 'Identification of a specific rate' },
          { id: '1131', name: 'Code list qualifier', description: 'Identification of a code list' },
          { id: '3055', name: 'Code list responsible agency, coded', description: 'Code identifying the agency responsible for a code list' },
          { id: '5278', name: 'Duty/tax/fee rate', description: 'Rate of duty/tax/fee' },
          { id: '5273', name: 'Duty/tax/fee rate basis identification', description: 'Identification of the basis for a duty/tax/fee rate' },
          { id: '1131', name: 'Code list qualifier', description: 'Identification of a code list' },
          { id: '3055', name: 'Code list responsible agency, coded', description: 'Code identifying the agency responsible for a code list' }
        ],
        examples: ['25']
      },
      { 
        id: '5305', 
        name: 'DUTY/TAX/FEE CATEGORY, CODED', 
        mandatory: false, 
        description: 'Code identifying the category of duty, tax or fee',
        examples: ['S']
      },
      { 
        id: '3446', 
        name: 'PARTY TAX IDENTIFIER', 
        mandatory: false, 
        description: 'Tax identification number of the party'
      }
    ],
    qualifiers: {
      '1': 'Customs duty',
      '2': 'Tax',
      '3': 'Statistical fee',
      '4': 'Mixed tax rate',
      '5': 'Duty/tax/fee paid by supplier',
      '6': 'Duty/tax/fee paid by buyer',
      '7': 'VAT',
      '8': 'Sales tax - state/provincial',
      '9': 'Sales tax - national',
      '10': 'Value added tax (VAT)',
      '11': 'Goods and services tax',
      '12': 'Excise duty',
      '13': 'Construction tax',
      '14': 'Energy fee',
      '15': 'Warranty related costs'
    },
    taxCategories: {
      'A': 'Mixed tax rate',
      'E': 'Exempt from tax',
      'S': 'Standard rate',
      'Z': 'Zero rated goods'
    },
    notes: 'The TAX segment is used to specify tax related information. In EANCOM, it is typically used to identify the tax type (e.g., VAT), the function of the tax (e.g., standard rate), and the applicable tax rate. The tax function qualifier specifies the type of tax, with code 7 commonly used for VAT. The tax category code identifies the category of tax, with S commonly used for standard rate VAT.',
    example: 'TAX+7+VAT+++25+S'
  },
  'TDT': {
    title: 'Transport Information',
    function: 'To specify the transport details related to movement of goods for the entire consignment.',
    elements: [
      { id: '8051', name: 'TRANSPORT STAGE QUALIFIER', mandatory: true, description: 'Code qualifying a stage of transport' },
      { id: '8028', name: 'MEANS OF TRANSPORT JOURNEY IDENTIFIER', mandatory: false, description: 'Identification of a journey of a means of transport' },
      { id: 'C220', name: 'MODE OF TRANSPORT', mandatory: false, description: 'Method of transport code and name' },
      { id: 'C228', name: 'TRANSPORT MEANS', mandatory: false, description: 'Type of means of transport code and name' },
      { id: 'C040', name: 'CARRIER', mandatory: false, description: 'Identification of the carrier' },
      { id: '8101', name: 'TRANSIT DIRECTION, CODED', mandatory: false, description: 'Code specifying the direction of transport' },
      { id: 'C401', name: 'EXCESS TRANSPORTATION INFORMATION', mandatory: false, description: 'Information relating to excess transportation' },
      { id: 'C222', name: 'TRANSPORT IDENTIFICATION', mandatory: false, description: 'Identification of the means of transport' },
      { id: '8281', name: 'TRANSPORT MEANS OWNERSHIP, CODED', mandatory: false, description: 'Code specifying the ownership of the means of transport' }
    ],
    qualifiers: {
      '10': 'Pre-carriage transport',
      '20': 'Main-carriage transport',
      '30': 'On-carriage transport'
    },
    transportModes: {
      '1': 'Maritime transport',
      '2': 'Rail transport',
      '3': 'Road transport',
      '4': 'Air transport',
      '8': 'Inland water transport',
      '9': 'Mode not specified'
    },
    notes: 'This segment is used to specify the mode and means of transport for the entire consignment. Multiple TDT segments may be used when the consignment is transported via different modes.'
  },
  'CPS': {
    title: 'Consignment Packing Sequence',
    function: 'To identify the sequence in which physical packaging is presented in a consignment, and to identify how one level of packaging is packed in another level of packaging.',
    elements: [
      { id: '7164', name: 'HIERARCHICAL ID NUMBER', mandatory: true, description: 'Unique identifier of a hierarchical level' },
      { id: '7166', name: 'HIERARCHICAL PARENT ID', mandatory: false, description: 'Identifier of the parent level' },
      { id: '7075', name: 'PACKAGING HIERARCHY, CODED', mandatory: false, description: 'Code specifying a level of packaging in a hierarchy' }
    ],
    hierarchyCodes: {
      '1': 'Highest level',
      '2': 'Second level',
      '3': 'Third level',
      '4': 'Fourth level',
      '5': 'Fifth level'
    },
    notes: 'This segment is used to identify each physical packaging in a nested packaging hierarchy. Each CPS segment represents a level in the hierarchy, with linkage provided to its parent level. This is commonly used in despatch advices.'
  },
  'PAC': {
    title: 'Package',
    function: 'To describe the number and type of packages and physical attributes, including dimensions and packaging materials.',
    elements: [
      { id: '7224', name: 'NUMBER OF PACKAGES', mandatory: false, description: 'Number of physical units' },
      { id: 'C531', name: 'PACKAGING DETAILS', mandatory: false, description: 'Type and properties of packaging' },
      { id: 'C202', name: 'PACKAGE TYPE', mandatory: false, description: 'Type of package by code and/or name' },
      { id: 'C402', name: 'PACKAGE TYPE IDENTIFICATION', mandatory: false, description: 'Identification of the type of package/packages' },
      { id: 'C532', name: 'RETURNABLE PACKAGE DETAILS', mandatory: false, description: 'Details of a returnable package' }
    ],
    packageTypes: {
      'BX': 'Box',
      'CS': 'Case',
      'CT': 'Carton',
      'PK': 'Package',
      'PL': 'Pallet',
      'CR': 'Crate',
      'RL': 'Reel',
      'BG': 'Bag',
      'BR': 'Barrel',
      'BT': 'Bottle',
      'CN': 'Container'
    },
    notes: 'This segment is used to provide information about the physical packaging of the goods. It may contain information about quantity, type, dimensions, and materials used.'
  },
  'FII': {
    title: 'Financial Institution Information',
    function: 'To identify an account and a related financial institution.',
    elements: [
      { id: '3035', name: 'PARTY QUALIFIER', mandatory: true, description: 'Code giving specific meaning to the party' },
      { id: 'C078', name: 'ACCOUNT IDENTIFICATION', mandatory: false, description: 'Identification of the account' },
      { id: 'C088', name: 'INSTITUTION IDENTIFICATION', mandatory: false, description: 'Identification of a financial institution' },
      { id: '3207', name: 'COUNTRY, CODED', mandatory: false, description: 'Identification of the country' }
    ],
    qualifiers: {
      'BF': 'Beneficiary\'s bank',
      'DP': 'Receiving financial institution',
      'RH': 'Clearing house',
      'PB': 'Ordered bank',
      'PE': 'Payee',
      'PR': 'Payer',
      'RB': 'Receiving bank',
      'BB': 'Issuing bank'
    },
    notes: 'This segment is used to identify financial institution information related to the transaction. It typically specifies the bank accounts involved in a payment transaction.',
    example: 'FII+RH+15060988126:BK'
  },
  'PYT': {
    title: 'Payment Terms',
    function: 'To specify the payment terms.',
    elements: [
      { id: '4279', name: 'PAYMENT TERMS TYPE QUALIFIER', mandatory: true, description: 'Identification of the type of payment terms' },
      { id: 'C019', name: 'PAYMENT TERMS', mandatory: false, description: 'Terms of payment information' },
      { id: '2475', name: 'PAYMENT TIME REFERENCE, CODED', mandatory: false, description: 'Code giving specific meaning to a time reference' },
      { id: '2009', name: 'TIME RELATION, CODED', mandatory: false, description: 'Code qualifying the relationship of time' },
      { id: '2151', name: 'TYPE OF PERIOD, CODED', mandatory: false, description: 'Code indicating the type of period' },
      { id: '2152', name: 'NUMBER OF PERIODS', mandatory: false, description: 'Count of the number of periods' }
    ],
    qualifiers: {
      '1': 'Basic',
      '2': 'End of month',
      '3': 'Fixed date',
      '4': 'Deferred',
      '5': 'Discount',
      '6': 'Mixed',
      '7': 'Extended',
      '8': 'Basic discount offered',
      '9': 'Proximo',
      '10': 'Instant',
      '11': 'Elective',
      '12': 'Tenth day in the month, unless that day is not a business day, in which case payment shall be made on the first preceding business day',
      '13': 'Cash with order (C.W.O.)',
      '14': 'Cash on delivery (C.O.D.)'
    },
    notes: 'This segment is used to specify the terms of payment applicable to the transaction. It defines how and when payment should be made.'
  },
  'PCD': {
    title: 'Percentage Details',
    function: 'To specify percentage information.',
    elements: [
      { id: 'C501', name: 'PERCENTAGE DETAILS', mandatory: true, description: 'Percentage information' }
    ],
    qualifiers: {
      '1': 'Allowance',
      '2': 'Charge',
      '3': 'Allowance or charge',
      '4': 'Discount',
      '5': 'Interest',
      '6': 'Material surcharge',
      '7': 'Tax',
      '8': 'Weight',
      '9': 'Volume',
      '10': 'Monetary amount',
      '11': 'Quantity',
      '12': 'Insurance',
      '13': 'Packaging',
      '14': 'Transport',
      '15': 'Banking charge',
      '16': 'Minimum commission'
    },
    notes: 'This segment is used to specify percentages applicable to the transaction, such as tax rates, discount percentages, or interest rates.'
  },
  'ALC': {
    title: 'Allowance or Charge',
    function: 'To identify the charge or allowance and to specify the relevant adjustment to the price or amount.',
    elements: [
      { id: '5463', name: 'ALLOWANCE OR CHARGE QUALIFIER', mandatory: true, description: 'Code qualifying the type of allowance or charge' },
      { id: 'C552', name: 'ALLOWANCE/CHARGE INFORMATION', mandatory: false, description: 'Identification of allowance/charge information' },
      { id: '4471', name: 'SETTLEMENT, CODED', mandatory: false, description: 'Code specifying the method of settlement' },
      { id: '1227', name: 'CALCULATION SEQUENCE INDICATOR, CODED', mandatory: false, description: 'Code specifying a calculation sequence' },
      { id: 'C214', name: 'SPECIAL SERVICES IDENTIFICATION', mandatory: false, description: 'Identification of a special service' }
    ],
    qualifiers: {
      'A': 'Allowance',
      'C': 'Charge',
      'N': 'No allowance or charge'
    },
    specialServiceCodes: {
      'AA': 'Advertising allowance - Allowance for advertising',
      'ABL': 'Additional packaging - Charge for extra packaging',
      'ADR': 'Administration',
      'ADT': 'Advisement',
      'CAC': 'Cataloguing services',
      'CAG': 'Coin-a-drink machine',
      'CAI': 'Competitive allowance',
      'CAL': 'Containerization',
      'CAM': 'Container deposit charge',
      'CAN': 'Cleaning after use',
      'CAP': 'Collection/pickup',
      'CAS': 'Cassette',
      'CAT': 'Cataloguing',
      'DI': 'Discount - Reduction from a usual amount',
      'EAA': 'Early buy allowance - Discount for ordering early',
      'EAB': 'Early payment allowance - Discount for early payment',
      'FC': 'Freight charge - Charge for transporting goods',
      'FI': 'Finance charge - Charge for financial services',
      'HD': 'Handling - Charge for the handling of goods',
      'PAD': 'Pallet charge - Charge for pallet use',
      'QD': 'Quantity discount - Discount based on quantity purchased',
      'RAA': 'Rebate',
      'SAB': 'Special credit agreement',
      'TAE': 'Transport emergency',
      'TD': 'Trade discount',
      'TX': 'Tax'
    },
    notes: 'This segment is used to specify allowances and charges that apply to the transaction. It can relate to the entire transaction, a group of items, or a single line item. The allowance or charge can be specified as a fixed amount or a percentage.'
  },
  'PAT': {
    title: 'Payment Terms Basis',
    function: 'To specify the payment terms basis and relevant dates for a transaction.',
    elements: [
      { id: '4279', name: 'PAYMENT TERMS TYPE QUALIFIER', mandatory: true, description: 'Identification of the type of payment terms' },
      { id: 'C110', name: 'PAYMENT TERMS', mandatory: false, description: 'Terms of payment information' },
      { id: 'C112', name: 'TERMS/TIME INFORMATION', mandatory: false, description: 'Information related to the time periods of terms' }
    ],
    qualifiers: {
      '1': 'Basic',
      '2': 'End of month',
      '3': 'Fixed date',
      '4': 'Deferred',
      '5': 'Discount',
      '6': 'Mixed',
      '7': 'Extended',
      '8': 'Basic discount offered',
      '9': 'Proximo',
      '10': 'Instant',
      '11': 'Elective',
      '12': 'Tenth day in the month',
      '13': 'Cash with order (C.W.O.)',
      '14': 'Cash on delivery (C.O.D.)',
      '15': 'Advance payment',
      '16': 'By arrangement',
      '17': 'Open account',
      '18': 'Pre-payment',
      '20': 'Penalty terms',
      '22': 'Discount (specify in DTM)',
      '23': 'Fixed day of month',
      '41': 'Same month',
      '42': 'Same week'
    },
    notes: 'This segment is used to specify the basis of terms of payment, such as due date calculation and discounts for the whole transaction. The PAT segment differs from the PYT segment, as PAT defines the terms basis, whereas PYT specifies the actual payment terms.'
  },
  'TOD': {
    title: 'Terms of Delivery',
    function: 'To specify the terms of delivery for a transaction.',
    elements: [
      { id: '4055', name: 'TERMS OF DELIVERY OR TRANSPORT, CODED', mandatory: false, description: 'Code specifying the terms of delivery or transport' },
      { id: '4215', name: 'TRANSPORT CHARGES METHOD OF PAYMENT, CODED', mandatory: false, description: 'Code specifying the payment method for transport charges' },
      { id: 'C100', name: 'TERMS OF DELIVERY OR TRANSPORT', mandatory: false, description: 'Terms of delivery or transport' }
    ],
    qualifiers: {
      'CFR': 'Cost and Freight',
      'CIF': 'Cost, Insurance and Freight',
      'CIP': 'Carriage and Insurance Paid To',
      'CPT': 'Carriage Paid To',
      'DAF': 'Delivered At Frontier',
      'DAP': 'Delivered At Place',
      'DAT': 'Delivered At Terminal',
      'DDP': 'Delivered Duty Paid',
      'DDU': 'Delivered Duty Unpaid',
      'DEQ': 'Delivered Ex Quay',
      'DES': 'Delivered Ex Ship',
      'EXW': 'Ex Works',
      'FAS': 'Free Alongside Ship',
      'FCA': 'Free Carrier',
      'FOB': 'Free On Board'
    },
    transportChargesCodes: {
      'A': 'Paid by customer',
      'DF': 'Defined by buyer and seller',
      'PB': 'Paid by buyer',
      'PC': 'Paid by consignee',
      'PE': 'Payable elsewhere',
      'PO': 'Paid by supplier',
      'PP': 'Paid by payee',
      'PU': 'Paid by debtor'
    },
    notes: 'This segment is used to specify the terms of delivery for the transaction. It can include Incoterms codes that define the transfer of risks and costs between buyer and seller, and the responsibility for transport costs.'
  },
  'LOC': {
    title: 'Place/Location Identification',
    function: 'To identify a place or location and specify related locations.',
    elements: [
      { id: '3227', name: 'PLACE/LOCATION QUALIFIER', mandatory: true, description: 'Code identifying the function of the place/location' },
      { id: 'C517', name: 'LOCATION IDENTIFICATION', mandatory: false, description: 'Identification of a place by code or name' },
      { id: 'C519', name: 'RELATED LOCATION ONE IDENTIFICATION', mandatory: false, description: 'Identification of the first related place/location by code or name' },
      { id: 'C553', name: 'RELATED LOCATION TWO IDENTIFICATION', mandatory: false, description: 'Identification of the second related place/location by code or name' },
      { id: '5479', name: 'RELATION, CODED', mandatory: false, description: 'Code for the relation between the first and the second related location' }
    ],
    qualifiers: {
      '1': 'Place of terms of delivery',
      '2': 'Payment place',
      '5': 'Place of departure',
      '7': 'Place of delivery',
      '9': 'Place of processing',
      '10': 'Place of loading',
      '11': 'Place of discharge',
      '14': 'Location of goods',
      '17': 'Border crossing place',
      '18': 'Country of first destination',
      '19': 'Country of last destination',
      '22': 'Customs office of clearance',
      '24': 'Port of discharge',
      '27': 'Country of origin',
      '28': 'Country of destination',
      '35': 'Country of exportation/despatch',
      '36': 'Country of ultimate destination',
      '42': 'Customs office of exit',
      '43': 'Customs office of departure',
      '92': 'Routing',
      '118': 'Country of production'
    },
    notes: 'This segment is used to identify places or locations related to the transaction, such as place of delivery, place of origin, or country of destination.'
  },
  'FTX': {
    title: 'Free Text',
    function: 'To provide free-form text information relevant to the transaction.',
    elements: [
      { id: '4451', name: 'TEXT SUBJECT QUALIFIER', mandatory: true, description: 'Code qualifying the subject of the text' },
      { id: '4453', name: 'TEXT FUNCTION, CODED', mandatory: false, description: 'Code specifying how to handle the text' },
      { id: 'C107', name: 'TEXT REFERENCE', mandatory: false, description: 'Coded text reference' },
      { id: 'C108', name: 'TEXT LITERAL', mandatory: false, description: 'Free text information' }
    ],
    qualifiers: {
      'AAA': 'Goods description',
      'AAB': 'Terms and conditions',
      'AAF': 'Dangerous goods additional information',
      'AAI': 'General information',
      'AAK': 'Price/sales catalogue information',
      'AAZ': 'Additional product information',
      'ACB': 'Additional information',
      'ACD': 'Reason',
      'ACE': 'Dispute',
      'ACF': 'Processing information',
      'DIN': 'Delivery instructions',
      'INV': 'Invoice instruction',
      'MKS': 'Additional marks/numbers information',
      'PMT': 'Payment information',
      'REG': 'Regulatory information',
      'SUR': 'Supplier remarks',
      'ZZZ': 'Mutually defined'
    },
    notes: 'This segment is used to provide free-form text information related to the transaction. It can include descriptions, handling instructions, payment notes, or any other relevant textual information not covered by structured segments.'
  },
  'DOC': {
    title: 'Document/Message Details',
    function: 'To identify documents and details relevant to the transaction.',
    elements: [
      { id: 'C002', name: 'DOCUMENT/MESSAGE NAME', mandatory: false, description: 'Identification of the document name/type by code or name' },
      { id: 'C503', name: 'DOCUMENT/MESSAGE DETAILS', mandatory: false, description: 'Identification of the document/message by its number, status, source or language' },
      { id: '3153', name: 'COMMUNICATION CHANNEL IDENTIFIER, CODED', mandatory: false, description: 'Code identifying the communication channel to be used for document delivery' },
      { id: '1220', name: 'NUMBER OF COPIES OF DOCUMENT REQUIRED', mandatory: false, description: 'Number of copies of the document that are required' },
      { id: '1218', name: 'NUMBER OF ORIGINALS OF DOCUMENT REQUIRED', mandatory: false, description: 'Number of originals of the document that are required' }
    ],
    documentCodes: {
      '1': 'Certificate of analysis',
      '2': 'Certificate of conformity',
      '3': 'Certificate of quality',
      '4': 'Test report',
      '5': 'Product performance report',
      '6': 'Product specification report',
      '7': 'Process data report',
      '8': 'Safety data sheet',
      '9': 'Veterinary certificate',
      '10': 'Phytosanitary certificate',
      '11': 'Quality specifications deviation report',
      '12': 'Mill certificate',
      '18': 'Movement certificate A.TR.1',
      '19': 'Certificate of quantity',
      '20': 'Insurance certificate',
      '21': 'Dangerous goods declaration',
      '22': 'Delivery verification certificate',
      '23': 'Import license',
      '24': 'Export license',
      '25': 'Certificate of shipment',
      '26': 'Combined certificate of value and origin',
      '27': 'Commercial invoice',
      '28': 'Customs invoice',
      '35': 'Letter of credit',
      '38': 'Consular invoice',
      '310': 'Instruction for returns',
      '315': 'Contract',
      '380': 'Commercial invoice',
      '810': 'Export license',
      '811': 'Import license',
      '861': 'Certificate of origin',
      '890': 'Dangerous goods declaration',
      '911': 'Import license',
      '915': 'Entry summary declaration',
      '916': 'Exit summary declaration',
      '925': 'Statistical document goods declaration',
      '929': 'Goods declaration for importation',
      '930': 'Goods declaration for exportation',
      '941': 'Export declaration'
    },
    notes: 'This segment is used to provide information about documents related to the transaction, such as certificates, licenses, or declarations. It can specify the type of document, its number, and whether originals or copies are required.'
  },
  'GIN': {
    title: 'Goods Identity Number',
    function: 'To provide specific identification numbers, either as single numbers or as ranges.',
    elements: [
      { id: '7405', name: 'IDENTITY NUMBER QUALIFIER', mandatory: true, description: 'Code qualifying the type of identity number' },
      { id: 'C208', name: 'IDENTITY NUMBER RANGE', mandatory: true, description: 'Range of identification numbers' },
      { id: 'C208', name: 'IDENTITY NUMBER RANGE', mandatory: false, description: 'Additional range of identification numbers' },
      { id: 'C208', name: 'IDENTITY NUMBER RANGE', mandatory: false, description: 'Additional range of identification numbers' },
      { id: 'C208', name: 'IDENTITY NUMBER RANGE', mandatory: false, description: 'Additional range of identification numbers' },
      { id: 'C208', name: 'IDENTITY NUMBER RANGE', mandatory: false, description: 'Additional range of identification numbers' }
    ],
    qualifiers: {
      'BN': 'Serial number',
      'BX': 'Batch number',
      'CN': 'Consignment number',
      'IZ': 'Shipment identifier',
      'ON': 'Order number',
      'PV': 'Promotional variant number',
      'SN': 'Serial number',
      'SRN': 'SSCC - Serial Shipping Container Code',
      'VC': 'Vendor item number',
      'VM': 'Vehicle identification number',
      'XA': 'Company/organisation identification number',
      'XB': 'Battery number',
      'XC': 'Service number',
      'XP': 'Previous batch number'
    },
    notes: 'This segment is used to identify specific items, batches, or shipments by their identity numbers. It is particularly useful in logistics and dispatch advice messages to provide traceability of items through the supply chain.'
  },
  'HAN': {
    title: 'Handling Instructions',
    function: 'To provide information on the physical handling requirements for the consignment.',
    elements: [
      { id: 'C524', name: 'HANDLING INSTRUCTIONS', mandatory: false, description: 'Instruction for handling of the item by code or in clear text' },
      { id: 'C218', name: 'HAZARDOUS MATERIAL', mandatory: false, description: 'Hazardous material information by code or in clear text' }
    ],
    handlingCodes: {
      '1': 'Handle with care',
      '2': 'Fragile',
      '3': 'Keep away from heat',
      '4': 'Keep away from cold',
      '5': 'Keep dry',
      '6': 'Keep away from direct sunlight',
      '7': 'Do not stack',
      '8': 'Do not drop',
      '9': 'Top part',
      '10': 'Keep upright',
      '11': 'Avoid heat',
      '12': 'Avoid frost',
      '13': 'Keep away from water',
      '14': 'Handle according to instructions',
      '15': 'Handle with care, liquid content',
      '20': 'Use no hooks',
      '21': 'No hand cart/trolley here',
      '22': 'Use no forks',
      '23': 'Do not clamp',
      '27': 'Stack maximum of (quantity)',
      '30': 'Full container load',
      '31': 'Less than container load',
      '37': 'Stack limited by weight',
      '42': 'Refrigeration required',
      '43': 'Keep at ambient temperature',
      '44': 'Special handling required for hazardous material',
      '45': 'Gas release danger',
      '46': 'Handle with care, contents easily damaged',
      '47': 'Handle with care, medical supplies',
      '48': 'Handle with care, technical equipment',
      '50': 'This way up',
      '51': 'Keep away from heat and radioactive sources',
      '52': 'Perspiration-proof packing required'
    },
    notes: 'This segment is used to specify handling instructions for the consignment or package. It can include physical handling requirements, storage conditions, and precautions for hazardous materials.'
  },
  'PCI': {
    title: 'Package Identification',
    function: 'To specify markings and labels on individual packages or physical units.',
    elements: [
      { id: '4233', name: 'MARKING INSTRUCTIONS, CODED', mandatory: false, description: 'Code specifying instructions for marking' },
      { id: 'C210', name: 'MARKS & LABELS', mandatory: false, description: 'Shipping marks on packages in free text' },
      { id: '8275', name: 'CONTAINER/PACKAGE STATUS, CODED', mandatory: false, description: 'Code specifying the status of the container or package' },
      { id: 'C827', name: 'TYPE OF MARKING', mandatory: false, description: 'Specification of the type of marking by name and/or code' }
    ],
    markingCodes: {
      '1': 'Do not mark the packaging',
      '2': 'Mark the packaging',
      '3': 'Mark the inner packaging',
      '4': 'Mark the outer packaging',
      '5': 'Mark each item',
      '6': 'Mark only first item',
      '7': 'Mark only last item',
      '8': 'Mark each level of the packaging',
      '9': 'Mark as specified',
      '10': 'Mark all packages the same',
      '11': 'Mark all inner packages',
      '12': 'Mark both inner and outer packages',
      '13': 'Mark as per instructions',
      '14': 'Mark on right side'
    },
    markingTypes: {
      'BN': 'Serial shipping container code (SSCC)',
      'ID': 'Identity number',
      'IN': 'Insurance policy number',
      'LN': 'License number',
      'MD': 'Manufacturer\'s article number',
      'PD': 'Promotional deal number',
      'SD': 'Supplier\'s article number',
      'SN': 'Serial number'
    },
    notes: 'This segment is used to specify the markings and labels on packages or physical units. It can include shipping marks, bar code information, handling instructions, or other package identifiers.'
  },
  'SCC': {
    title: 'Scheduling Conditions',
    function: 'To specify the scheduling conditions for delivery and transport of goods.',
    elements: [
      { id: '4017', name: 'DELIVERY PLAN STATUS INDICATOR, CODED', mandatory: true, description: 'Code specifying the status of a delivery plan' },
      { id: '4493', name: 'DELIVERY REQUIREMENTS, CODED', mandatory: false, description: 'Code specifying the delivery requirements' },
      { id: 'C329', name: 'PATTERN DESCRIPTION', mandatory: false, description: 'Description of a pattern by code or in clear text' }
    ],
    statusCodes: {
      '1': 'Firm schedule',
      '2': 'Forecast/planning schedule',
      '3': 'Updated schedule',
      '4': 'Release schedule',
      '5': 'Information schedule',
      '6': 'Provisional schedule',
      '7': 'Amendment schedule',
      '8': 'Split delivery schedule',
      '9': 'Schedule for provisional delivery',
      '10': 'Schedule for definitive purchase order',
      '11': 'Schedule according to contract'
    },
    requirementCodes: {
      '1': 'Previous delivery schedule still valid',
      '2': 'Previous delivery schedule to be treated as neutral',
      '3': 'Previous delivery schedule completely cancelled',
      '4': 'Previous delivery schedule partially cancelled, with details',
      '5': 'Previous delivery schedule completely or partially cancelled, with details',
      '6': 'Delivery overdue',
      '7': 'Delivery in process of execution (goods in transit)',
      '8': 'Delivery completed (goods arrived at scheduled destination)',
      '9': 'Delivery completed as scheduled',
      '10': 'Delivery partially completed (split delivery)',
      '11': 'Firm delivery',
      '12': 'Change in delivery schedule'
    },
    notes: 'This segment is used to specify the scheduling information or status of a delivery schedule. It is commonly used in delivery schedule messages to indicate if a schedule is firm, a forecast, or has special delivery requirements.'
  },
  'RCS': {
    title: 'Requirements and Conditions',
    function: 'To specify sector or industry requirements and conditions.',
    elements: [
      { id: '7293', name: 'SECTOR/SUBJECT IDENTIFICATION QUALIFIER', mandatory: true, description: 'Code qualifying the subject to which information applies' },
      { id: 'C550', name: 'REQUIREMENT/CONDITION IDENTIFICATION', mandatory: false, description: 'Identification of a requirement or condition by code and/or description' },
      { id: '1229', name: 'ACTION REQUEST/NOTIFICATION, CODED', mandatory: false, description: 'Code specifying the action to be taken or already taken' },
      { id: '3207', name: 'COUNTRY, CODED', mandatory: false, description: 'Identification of the name of a country by code' }
    ],
    sectorCodes: {
      '1': 'Aeronautics',
      '2': 'All sectors',
      '3': 'Automotive',
      '4': 'Consumer goods',
      '5': 'Chemical',
      '6': 'Construction',
      '7': 'Defense',
      '8': 'Energy',
      '9': 'Finance',
      '10': 'Health care',
      '11': 'Insurance',
      '12': 'Logistics',
      '13': 'Transportation',
      '14': 'Manufacturing',
      '15': 'Oil and gas',
      '16': 'Pharmacy',
      '17': 'Public sector',
      '18': 'Retail',
      '19': 'Telecommunications',
      '20': 'Tourism'
    },
    actionCodes: {
      '1': 'Added',
      '2': 'Changed',
      '3': 'Deleted',
      '4': 'Accepted',
      '5': 'Rejected',
      '6': 'Pending',
      '7': 'Amended',
      '8': 'Confirmed',
      '9': 'Not found',
      '10': 'Ready for transmission'
    },
    notes: 'This segment is used to specify requirements or conditions that apply to the transaction. It can indicate special requirements for a specific industry sector, or specify regulatory or commercial conditions.'
  },
  'MEA': {
    title: 'Measurements',
    function: 'To specify physical measurements, including dimension tolerances, weights, and counts.',
    elements: [
      { id: '6311', name: 'MEASUREMENT PURPOSE QUALIFIER', mandatory: true, description: 'Code qualifying the purpose of the measurement' },
      { id: 'C502', name: 'MEASUREMENT DETAILS', mandatory: false, description: 'Identification of measurement type by code and/or name' },
      { id: 'C174', name: 'VALUE/RANGE', mandatory: false, description: 'Measurement value and relevant minimum and maximum values' },
      { id: '7383', name: 'SURFACE/LAYER INDICATOR, CODED', mandatory: false, description: 'Code identifying the surface or layer of a product that is being measured' }
    ],
    qualifiers: {
      'AAA': 'Line item measurement',
      'AAB': 'Package dimensions (including packaging)',
      'AAC': 'Dimensions after packing, including items',
      'AAD': 'Unit dimensions',
      'AAE': 'Dimensions of stacked pallets',
      'AAF': 'Gross weight',
      'AAG': 'Package gross weight',
      'AAH': 'Package dimensions (excluding packages)',
      'AAI': 'Gross dimensions',
      'AAJ': 'Product dimensions',
      'AAL': 'Net weight',
      'AAW': 'Gross volume',
      'AAX': 'Nested item stacking level',
      'ABJ': 'Volume, liquid measure',
      'ABK': 'Dimensions when stacked on receptacle',
      'ABL': 'Package dimensions',
      'ABO': 'Inside dimensions',
      'ABP': 'Dimensions plus packaging',
      'ABQ': 'Stacked (with items) dimensions',
      'ABW': 'Outer dimensions',
      'HT': 'Height dimension',
      'LN': 'Length dimension',
      'PD': 'Physical dimensions',
      'TE': 'Temperature',
      'WD': 'Width dimension',
      'WT': 'Weight'
    },
    measurementUnits: {
      'KGM': 'Kilogram',
      'GRM': 'Gram',
      'LBR': 'Pound',
      'CMT': 'Centimeter',
      'MMT': 'Millimeter',
      'MTR': 'Meter',
      'INH': 'Inch',
      'FOT': 'Foot',
      'YRD': 'Yard',
      'CEL': 'Celsius',
      'FAH': 'Fahrenheit',
      'MTK': 'Square meter',
      'MTQ': 'Cubic meter',
      'LTR': 'Liter',
      'MLT': 'Milliliter'
    },
    notes: 'This segment is used to specify physical measurements of items, packages, or shipments. It can include dimensions, weights, volumes, temperatures, or any other measurable physical properties.'
  },
  'EQD': {
    title: 'Equipment Details',
    function: 'To identify equipment and provide equipment details.',
    elements: [
      { id: '8053', name: 'EQUIPMENT QUALIFIER', mandatory: true, description: 'Code qualifying the type of equipment' },
      { id: 'C237', name: 'EQUIPMENT IDENTIFICATION', mandatory: false, description: 'Identification of equipment by code and/or number' },
      { id: 'C224', name: 'EQUIPMENT SIZE AND TYPE', mandatory: false, description: 'Code and/or name identifying the size and type of equipment' },
      { id: '8077', name: 'EQUIPMENT SUPPLIER, CODED', mandatory: false, description: 'Code identifying the party that is the supplier of the equipment' },
      { id: '8249', name: 'EQUIPMENT STATUS, CODED', mandatory: false, description: 'Code specifying the status of the equipment' },
      { id: '8169', name: 'FULL/EMPTY INDICATOR, CODED', mandatory: false, description: 'Code indicating whether the equipment is full or empty' }
    ],
    qualifiers: {
      'AQ': 'Vehicle',
      'BB': 'Wagon',
      'BL': 'Container trailer',
      'BX': 'Box',
      'CN': 'Container',
      'CT': 'Carton',
      'PA': 'Pallet',
      'PL': 'Platform',
      'SW': 'Swap body',
      'TE': 'Trailer',
      'TL': 'Tank container',
      'TR': 'Tractor',
      'UL': 'ULD (Unit Load Device)'
    },
    statusCodes: {
      '1': 'Continental',
      '2': 'Export',
      '3': 'Import',
      '4': 'Remain on board',
      '5': 'Shifter',
      '6': 'To be loaded',
      '7': 'To be discharged',
      '8': 'Overlanded',
      '9': 'Short landed',
      '10': 'Original',
      '11': 'Substitute'
    },
    fullEmptyCodes: {
      '1': 'More than one quarter empty',
      '2': 'More than one quarter full',
      '3': 'Empty',
      '4': 'Full',
      '5': 'Full load',
      '6': 'Less than full load',
      '7': 'Full load with additional quantities available'
    },
    notes: 'This segment is used to specify details of equipment used in a transport operation. It can identify containers, vehicles, pallets, or other transport equipment.'
  },
  'SEQ': {
    title: 'Sequence Details',
    function: 'To provide information on sequence of items and delivery patterns.',
    elements: [
      { id: '1229', name: 'ACTION REQUEST/NOTIFICATION, CODED', mandatory: false, description: 'Code specifying the notification or requested action' },
      { id: 'C286', name: 'SEQUENCE INFORMATION', mandatory: false, description: 'Sequence information including number and position' }
    ],
    actionCodes: {
      '1': 'Added',
      '2': 'Deleted',
      '3': 'Changed',
      '4': 'No action',
      '5': 'Accepted without amendment',
      '6': 'Accepted with amendment',
      '7': 'Not accepted',
      '8': 'Schedule quantity increased',
      '9': 'Schedule quantity decreased',
      '10': 'Delivery date/time changed',
      '11': 'Message rejected (technical error)',
      '12': 'Message accepted'
    },
    notes: 'This segment is used to provide sequence numbers and delivery pattern details. It helps identify the position of items within a sequence and can specify actions related to each sequence position.'
  },
  'DGS': {
    title: 'Dangerous Goods',
    function: 'To identify dangerous goods and provide related information.',
    elements: [
      { id: '8273', name: 'DANGEROUS GOODS REGULATIONS, CODED', mandatory: false, description: 'Code identifying the regulation for the classification of dangerous goods' },
      { id: 'C205', name: 'HAZARD CODE', mandatory: false, description: 'Hazard code identification' },
      { id: 'C234', name: 'UNDG INFORMATION', mandatory: false, description: 'UN Dangerous Goods identifying number' },
      { id: '8246', name: 'DANGEROUS GOODS ADDITIONAL INFORMATION, CODED', mandatory: false, description: 'Additional hazard classification identifier' },
      { id: 'C223', name: 'DANGEROUS GOODS SHIPMENT FLASHPOINT', mandatory: false, description: 'Temperature at which a substance ignites' },
      { id: '8255', name: 'PACKING GROUP, CODED', mandatory: false, description: 'Code specifying the level of danger for which the packaging must cater' },
      { id: '8179', name: 'HAZARD IDENTIFICATION NUMBER, UPPER PART', mandatory: false, description: 'Upper part of the hazard identification number' },
      { id: '8178', name: 'HAZARD IDENTIFICATION NUMBER, LOWER PART', mandatory: false, description: 'Lower part of the hazard identification number' },
      { id: 'C235', name: 'HAZARD IDENTIFICATION PLACARD DETAILS', mandatory: false, description: 'Information on hazard identification markings' },
      { id: 'C236', name: 'DANGEROUS GOODS LABEL', mandatory: false, description: 'Markings identifying the type of hazardous goods' },
      { id: '8169', name: 'FULL/EMPTY INDICATOR, CODED', mandatory: false, description: 'Code indicating whether a piece of equipment is full or empty' }
    ],
    regulationCodes: {
      'ADR': 'European agreement on the international carriage of dangerous goods on road (ADR)',
      'CFR': 'Code of Federal Regulations',
      'IATA': 'International Air Transport Association (IATA)',
      'IMD': 'International Maritime Organization (IMO)',
      'RID': 'International rail transport of dangerous goods regulations',
      'TEC': 'Transport emergency chart',
      'UI': 'UK IMO book'
    },
    packingGroupCodes: {
      '1': 'Great danger',
      '2': 'Medium danger',
      '3': 'Minor danger',
      'I': 'High danger',
      'II': 'Medium danger',
      'III': 'Low danger'
    },
    notes: 'This segment is used to identify dangerous goods and provide relevant information for their handling, including hazard classification, UN number, packing group, and regulatory requirements.'
  },
  'STS': {
    title: 'Status',
    function: 'To specify the status of an object or service.',
    elements: [
      { id: '9015', name: 'STATUS CATEGORY, CODED', mandatory: false, description: 'Code specifying the category of status' },
      { id: 'C601', name: 'STATUS DETAILS', mandatory: false, description: 'Identification of the status details' },
      { id: 'C555', name: 'STATUS EVENT', mandatory: false, description: 'Identification of the status event' },
      { id: 'C556', name: 'STATUS REASON', mandatory: false, description: 'Identification of the reason for the status' },
      { id: 'C556', name: 'STATUS REASON', mandatory: false, description: 'Identification of the reason for the status' },
      { id: 'C556', name: 'STATUS REASON', mandatory: false, description: 'Identification of the reason for the status' },
      { id: 'C556', name: 'STATUS REASON', mandatory: false, description: 'Identification of the reason for the status' },
      { id: 'C556', name: 'STATUS REASON', mandatory: false, description: 'Identification of the reason for the status' }
    ],
    statusCategoryCodes: {
      '1': 'Forecast',
      '2': 'Confirmed',
      '3': 'Firm',
      '4': 'Provisional',
      '5': 'Action required',
      '6': 'Completed',
      '7': 'Fixed',
      '8': 'In process',
      '9': 'Released',
      '10': 'Rejected',
      '11': 'Problem',
      '12': 'Cancelled',
      '13': 'Preliminary',
      '14': 'Active'
    },
    notes: 'This segment is used to specify the status of an object, document, or transaction. It can indicate whether an item is approved, rejected, pending, or in some other status that is relevant to the business process.'
  },
  'AGR': {
    title: 'Agreement Identification',
    function: 'To specify a contract or agreement and details directly related to it.',
    elements: [
      { id: '9419', name: 'SERVICE AGREEMENT TYPE, CODED', mandatory: false, description: 'Code specifying the type of service agreement' },
      { id: 'C814', name: 'AGREEMENT TYPE IDENTIFICATION', mandatory: false, description: 'Identification of an agreement by code and/or description' },
      { id: 'C972', name: 'AGREEMENT REVISION NUMBER', mandatory: false, description: 'Revision number of an agreement' },
      { id: '6341', name: 'EXCHANGE RATE CURRENCY MARKET IDENTIFIER, CODED', mandatory: false, description: 'Identification of the currency market' },
      { id: '9419', name: 'SERVICE AGREEMENT TYPE, CODED', mandatory: false, description: 'Code specifying the type of service agreement' }
    ],
    serviceAgreementCodes: {
      '1': 'Standard agreement',
      '2': 'Specific agreement',
      '3': 'Special service agreement',
      '4': 'Lease agreement',
      '5': 'Purchase agreement',
      '6': 'Sales agreement',
      '7': 'Framework agreement',
      '8': 'Consolidation agreement',
      '9': 'Processing agreement',
      '10': 'Interchange agreement',
      '11': 'Transportation agreement',
      '12': 'Warehouse agreement',
      '13': 'Equipment agreement',
      '14': 'EDI agreement',
      '15': 'Financial agreement',
      '16': 'Global contract',
      '17': 'Framework contract'
    },
    notes: 'This segment is used to identify an agreement or contract related to the transaction. It can specify the type of agreement, agreement number, and revision information.'
  }
};

/**
 * Repository of code values and their meanings for different segments and elements
 * This provides more detailed explanations of specific codes used in EDIFACT
 */
const codeValueMeanings = {
  // Package types (used in PAC segment)
  'packageTypes': {
    '1': 'Bag',
    '2': 'Tank',
    '3': 'Aerosol',
    '4': 'Pallet, modular',
    '5': 'Pallet, 40x48',
    '6': 'Case',
    '7': 'Box, isothermic',
    '8': 'Pallet, 100x110',
    '9': 'Pallet, AS',
    '10': 'Pallet, ISO 1',
    '11': 'Pallet, ISO 2',
    '13': 'Euro pallet',
    '14': 'Expandable bag',
    '15': 'Container',
    '16': 'Can',
    '17': 'Capsule',
    '19': 'Unit load device',
    '20': 'Pallet, CP1',
    '21': 'Pallet, CP2',
    '22': 'Pallet, CP3',
    '23': 'Pallet, CP4',
    '24': 'Synthetic pallet ISO 1',
    '25': 'Synthetic pallet ISO 2',
    '26': 'Pallet, upright',
    '27': 'Bottle crate, bottle rack',
    '28': 'Europallet (80 x 120 cm)',
    '29': 'Roll pallet (wire wheeled carrier)',
    '30': 'Semi-bulk container, rigid plastic',
    '31': 'Crate',
    '32': 'Bottle, non-protected, cylindrical',
    '33': 'Carton',
    '34': 'Roll container',
    '35': 'Wheeled pallet',
    '36': 'Clamshell',
    '37': 'Roll',
    '38': 'Slip sheet',
    '39': 'Semi-bulk container, flexible',
    '40': 'Tub',
    '41': 'Wooden barrel',
    '42': 'Bulk, scrap metal',
    '43': 'Bulk, sand',
    '44': 'Bulk, wool',
    '45': 'Bulk, coal',
    '46': 'Bulk, grain',
    '47': 'Bulk, chemicals',
    '48': 'Bin',
    '49': 'Piece',
    '50': 'Bulk, chemically prepared wood pulp',
    '51': 'Multiple paper layers',
    '52': 'Mechanical paper layers',
    '53': 'Different paper layers',
    '201': 'Pallet ISO 1 - 1/1 EURO Pallet',
    '202': 'Pallet ISO 2 - 2/1 EURO Pallet',
    '203': 'One-Quarter EURO Pallet',
    '204': 'One-Half EURO Pallet',
    '205': 'One-Eighth EURO Pallet',
    '210': 'Synthetic pallet',
    '211': 'Wooden pallet',
    '212': 'Cardboard pallet',
    '213': 'Metal pallet',
    '214': 'Plastic pallet',
    '215': 'Paper pallet',
    '216': 'Composite pallet',
    '217': 'Plywood pallet',
    '218': 'Fibreboard pallet',
    '219': 'Wooden pallet, with reinforcement',
    '220': 'Synthetic pallet, with reinforcement',
    '221': 'Cardboard pallet, with reinforcement',
    'BX': 'Box',
    'CS': 'Case',
    'CT': 'Carton',
    'PK': 'Package',
    'PL': 'Pallet',
    'CR': 'Crate',
    'RL': 'Reel',
    'BG': 'Bag',
    'BR': 'Barrel',
    'BT': 'Bottle',
    'CN': 'Container'
  },
  
  // Document types (used in BGM segment)
  'documentTypes': {
    '1': 'Invoice',
    '2': 'Purchase order',
    '3': 'Inventory report',
    '4': 'Delivery note',
    '5': 'Shipping notice',
    '6': 'Despatch advice',
    '7': 'Receiving advice',
    '8': 'Price/sales catalogue',
    '9': 'Request for quote',
    '10': 'Quotation',
    '11': 'Order acknowledgement',
    '12': 'Order change request',
    '13': 'Order response',
    '220': 'Order',
    '231': 'Purchase order response',
    '240': 'Delivery instruction',
    '241': 'Delivery order',
    '310': 'Offer/quotation',
    '351': 'Despatch advice',
    '380': 'Commercial invoice',
    '481': 'Remittance advice'
  },
  
  // Transport modes (used in TDT segment)
  'transportModes': {
    '0': 'Transport mode not specified',
    '1': 'Maritime transport',
    '2': 'Rail transport',
    '3': 'Road transport',
    '4': 'Air transport',
    '5': 'Mail',
    '6': 'Multimodal transport',
    '7': 'Fixed transport installations (pipelines)',
    '8': 'Inland water transport',
    '9': 'Transport mode not applicable'
  },
  
  // Country codes (used in NAD, ALI segments)
  'countryCodes': {
    'AD': 'Andorra',
    'AE': 'United Arab Emirates',
    'AF': 'Afghanistan',
    'AG': 'Antigua and Barbuda',
    'AI': 'Anguilla',
    'AL': 'Albania',
    'AM': 'Armenia',
    'AO': 'Angola',
    'AR': 'Argentina',
    'AT': 'Austria',
    'AU': 'Australia',
    'BE': 'Belgium',
    'BG': 'Bulgaria',
    'BR': 'Brazil',
    'CA': 'Canada',
    'CH': 'Switzerland',
    'CN': 'China',
    'CZ': 'Czech Republic',
    'DE': 'Germany',
    'DK': 'Denmark',
    'ES': 'Spain',
    'FI': 'Finland',
    'FR': 'France',
    'GB': 'United Kingdom',
    'GR': 'Greece',
    'HK': 'Hong Kong',
    'HR': 'Croatia',
    'HU': 'Hungary',
    'IE': 'Ireland',
    'IL': 'Israel',
    'IN': 'India',
    'IS': 'Iceland',
    'IT': 'Italy',
    'JP': 'Japan',
    'KR': 'Korea, Republic of',
    'LT': 'Lithuania',
    'LU': 'Luxembourg',
    'LV': 'Latvia',
    'MX': 'Mexico',
    'MY': 'Malaysia',
    'NL': 'Netherlands',
    'NO': 'Norway',
    'NZ': 'New Zealand',
    'PL': 'Poland',
    'PT': 'Portugal',
    'RO': 'Romania',
    'RU': 'Russian Federation',
    'SE': 'Sweden',
    'SG': 'Singapore',
    'SI': 'Slovenia',
    'SK': 'Slovakia',
    'TR': 'Turkey',
    'UA': 'Ukraine',
    'US': 'United States',
    'ZA': 'South Africa'
  },
  
  // Message types (used in UNH segment)
  'messageTypes': {
    'ORDERS': 'Purchase order message',
    'ORDRSP': 'Purchase order response message',
    'INVOIC': 'Invoice message',
    'DESADV': 'Despatch advice message',
    'RECADV': 'Receiving advice message',
    'QUOTES': 'Quote message',
    'PRICAT': 'Price/sales catalogue message',
    'REMADV': 'Remittance advice message',
    'SLSRPT': 'Sales data report message',
    'INVRPT': 'Inventory report message',
    'DELFOR': 'Delivery schedule message',
    'PAXLST': 'Passenger list message',
    'PAYDUC': 'Payroll deductions advice message',
    'PAYEXT': 'Extended payment order message',
    'PAYORD': 'Payment order message'
  },

  // Measurement unit codes (used in QTY, MEA segments)
  'measurementUnits': {
    'KGM': 'Kilogram',
    'GRM': 'Gram',
    'MGM': 'Milligram',
    'TNE': 'Metric ton (tonne)',
    'LBR': 'Pound',
    'ONZ': 'Ounce',
    'CMT': 'Centimeter',
    'MMT': 'Millimeter',
    'MTR': 'Meter',
    'INH': 'Inch',
    'FOT': 'Foot',
    'YRD': 'Yard',
    'PCE': 'Piece',
    'PK': 'Pack',
    'CT': 'Carton',
    'BX': 'Box',
    'EA': 'Each',
    'DZN': 'Dozen',
    'MLT': 'Milliliter',
    'LTR': 'Liter',
    'GLL': 'Gallon',
    'FTK': 'Square foot',
    'MTK': 'Square meter',
    'MTQ': 'Cubic meter',
    'FTQ': 'Cubic foot',
    'SEC': 'Second',
    'MIN': 'Minute',
    'HUR': 'Hour',
    'DAY': 'Day',
    'MON': 'Month',
    'ANN': 'Year'
  },
  
  // Currency codes (used in CUX segment)
  'currencyCodes': {
    'USD': 'US Dollar',
    'EUR': 'Euro',
    'GBP': 'Pound Sterling',
    'JPY': 'Japanese Yen',
    'AUD': 'Australian Dollar',
    'CAD': 'Canadian Dollar',
    'CHF': 'Swiss Franc',
    'CNY': 'Chinese Yuan',
    'INR': 'Indian Rupee',
    'SEK': 'Swedish Krona',
    'DKK': 'Danish Krone',
    'NOK': 'Norwegian Krone',
    'MXN': 'Mexican Peso',
    'BRL': 'Brazilian Real',
    'ZAR': 'South African Rand',
    'RUB': 'Russian Ruble',
    'PLN': 'Polish Zloty',
    'TRY': 'Turkish Lira',
    'SGD': 'Singapore Dollar',
    'HKD': 'Hong Kong Dollar',
    'NZD': 'New Zealand Dollar'
  }
};

/**
 * Common qualifier codes explanations based on EANCOM documentation
 * These are frequently used qualifier codes across different segments
 */
const qualifierCodesExplanations = {
  // Party qualifier codes (used in NAD, FII segments)
  'partyQualifiers': {
    'BY': 'Buyer - Party to whom merchandise and/or service is sold',
    'SE': 'Seller - Party selling merchandise or services to a buyer',
    'ST': 'Ship to - Party to whom goods are to be delivered',
    'SU': 'Supplier - Party who supplies goods and/or services',
    'CN': 'Consignee - Party to whom goods are to be delivered',
    'DP': 'Delivery party - Party to which goods should be delivered',
    'IV': 'Invoicee - Party to whom an invoice is issued',
    'PE': 'Payee - Party to whom payment is made',
    'UC': 'Ultimate consignee - Final party to which goods are to be delivered',
    'II': 'Issuer of invoice - Party issuing an invoice',
    'UD': 'Ultimate customer - Final recipient of goods in a multi-step delivery process'
  },
  
  // Reference qualifier codes (used in RFF segment)
  'referenceQualifiers': {
    'ON': 'Order number - Reference number assigned by the buyer to an order',
    'VN': 'Order number (supplier) - Reference number assigned by the supplier to an order',
    'CT': 'Contract number - Reference number identifying a specific contract',
    'PO': 'Purchase order number - Reference number assigned by the purchase order issuer',
    'IV': 'Invoice number - Reference number assigned by the seller to an invoice',
    'ADU': 'Despatch unit - Shipping unit',
    'VA': 'VAT registration number - Unique identifier assigned to a person by a VAT authority',
    'CR': 'Customer reference number - Reference number assigned by the customer to a transaction',
    'AAK': 'Despatch advice number - Reference number assigned to a despatch advice',
    'AAS': 'Transport document number - Reference number assigned to a transport document',
    'AID': 'Certificate of conformity - Certificate certifying the conformity to predefined definitions',
    'DL': 'Debit note number - Reference number assigned to a debit note',
    'CLI': 'Client number - Unique identifier assigned to a client',
    'AAJ': 'Delivery order number - Reference number assigned to a delivery order',
    'PQ': 'Payment reference - Reference number assigned to a payment',
    'AAB': 'Proforma invoice number - Reference number assigned to a proforma invoice'
  },
  
  // Date/time qualifier codes (used in DTM segment)
  'dateTimeQualifiers': {
    '2': 'Delivery date/time, requested - Date on which buyer requests goods to be delivered',
    '3': 'Invoice date/time - Date when the invoice is issued',
    '4': 'Order date/time - Date when an order is placed',
    '7': 'Effective date/time - Date when the information becomes effective',
    '10': 'Shipment date/time, requested - Date on which buyer requests goods to be shipped',
    '11': 'Dispatch date and/or time - Date/time when goods are dispatched by the supplier',
    '12': 'Terms discount due date/time - Date by which payment should be made to obtain specified discount',
    '13': 'Terms discount due date/time - Latest date for discount terms to apply to payment',
    '17': 'Delivery date/time, estimated - Estimated date of delivery',
    '35': 'Delivery date/time, actual - Date when goods were actually delivered',
    '36': 'Expiry date - Date when something expires (e.g., credit, promotion, etc.)',
    '50': 'Goods receipt date/time - Date when goods were received',
    '63': 'Latest delivery date/time - Latest acceptable date for delivery',
    '64': 'Earliest delivery date/time - Earliest acceptable date for delivery',
    '69': 'Delivery date/time, promised for - Date supplier promised delivery',
    '71': 'Delivery date/time, requested for (after and including) - Delivery not to be before this date',
    '72': 'Delivery date/time, requested for (before and including) - Delivery not to be after this date',
    '76': 'Production/manufacture date - Date when the product was manufactured or produced',
    '137': 'Document/message date/time - Date when the document was created',
    '140': 'Payment due date - Date on which payment is due',
    '171': 'Reference date/time - Date used for reference purposes',
    '200': 'Pick-up/collection date/time - Date/time when goods are to be picked up or collected',
    '263': 'Invoicing period - Period for which the invoice is issued',
    '325': 'Tax period - Period for tax reporting'
  },
  
  // Payment terms qualifier codes (used in PAT, PYT segments)
  'paymentTermsQualifiers': {
    '1': 'Basic - Payment terms as baseline for calculation',
    '2': 'End of month - Payment due at the end of the month',
    '3': 'Fixed date - Payment due on a fixed date',
    '4': 'Deferred - Payment is deferred',
    '5': 'Discount - Payment terms are based on discounts allowed',
    '8': 'Basic discount offered - Basic payment terms with a discount',
    '10': 'Instant - Immediate payment',
    '13': 'Cash with order (C.W.O.) - Payment made at the time of order',
    '14': 'Cash on delivery (C.O.D.) - Payment made at the time of delivery'
  },
  
  // Monetary amount qualifier codes (used in MOA segment)
  'monetaryAmountQualifiers': {
    '9': 'Amount due/amount payable - Amount to be paid',
    '22': 'Cash discount amount - Amount of the discount for the payment terms',
    '77': 'Invoice amount - Total amount of an invoice',
    '79': 'Total line items amount - Sum of all line items in a document',
    '86': 'Message total monetary amount - Total monetary value of a message',
    '118': 'Taxable amount - Amount on which tax is based',
    '124': 'Tax amount - Amount of tax',
    '125': 'Taxable amount - excluding tax',
    '128': 'Total amount',
    '131': 'Total charges/allowances - Sum of all charge and allowance amounts',
    '139': 'Total payment discount - Total amount of the payment discount',
    '150': 'Allowance/charge amount',
    '176': 'Message total duty/tax/fee amount',
    '203': 'Total amount subject to payment discount',
    '260': 'Total value of expected payments - The total monetary amount of expected payments',
    '304': 'Order total',
    '5': 'Adjusted amount',
    '8': 'Applicable amount',
    '12': 'Allowance amount',
    '15': 'Amount of lower value equivalent',
    '20': 'Charge amount',
    '21': 'Charge or allowance amount',
    '25': 'Charge/allowance amount',
    '52': 'Discount amount',
    '54': 'Discount amount due to incident',
    '57': 'Equivalent amount',
    '66': 'Customs value amount',
    '166': 'Tax basis amount',
    '369': 'Goods and services tax amount',
    '388': 'Total amount including Value Added Tax (VAT)',
    'NET': 'Net amount - Amount after all allowances and charges, and before tax'
  },
  
  // Quantity qualifier codes (used in QTY segment)
  'quantityQualifiers': {
    '1': 'Discrete quantity - Quantity expressed in a discrete form',
    '11': 'Split quantity - Quantity to be divided or split',
    '12': 'Despatch quantity - Quantity to be despatched or being despatched',
    '21': 'Ordered quantity - Quantity ordered by the buyer',
    '46': 'Delivered quantity - Quantity delivered as per delivery instructions',
    '47': 'Invoiced quantity - Quantity invoiced',
    '52': 'Quantity per pack - Number of items contained in a pack',
    '59': 'Number of consumer units in a trading unit - Number of consumer units',
    '61': 'Return quantity - Quantity returned',
    '83': 'Backorder quantity - Quantity on back order',
    '113': 'Quantity shipped, to date - Total quantity shipped to date',
    '192': 'Free goods quantity - Quantity provided free of charge'
  },
  
  // Location qualifier codes (used in LOC segment)
  'locationQualifiers': {
    '1': 'Place of terms of delivery - Location where the delivery terms apply',
    '2': 'Payment place - Location where payment is to be made',
    '7': 'Place of delivery - Place to which the goods are to be delivered',
    '10': 'Place of loading - Place where goods are loaded onto a means of transport',
    '11': 'Place of discharge - Place where goods are unloaded from a means of transport',
    '14': 'Location of goods - Place where goods are currently located',
    '27': 'Country of origin - Country from which the goods originate',
    '28': 'Country of destination - Country to which the goods are destined'
  },
  
  // Allowance or charge qualifier codes (used in ALC segment)
  'allowanceChargeQualifiers': {
    'A': 'Allowance - Specific amount taken off the invoice value',
    'C': 'Charge - Additional amount added to the invoice value',
    'N': 'No allowance or charge - No allowance or charge applies'
  },
  
  // Special service codes (used in ALC segment)
  'specialServiceCodes': {
    'AA': 'Advertising allowance - Allowance for advertising',
    'ABL': 'Additional packaging - Charge for extra packaging',
    'ADR': 'Administration',
    'ADT': 'Advisement',
    'CAC': 'Cataloguing services',
    'CAG': 'Coin-a-drink machine',
    'CAI': 'Competitive allowance',
    'CAL': 'Containerization',
    'CAM': 'Container deposit charge',
    'CAN': 'Cleaning after use',
    'CAP': 'Collection/pickup',
    'CAS': 'Cassette',
    'CAT': 'Cataloguing',
    'DI': 'Discount - Reduction from a usual amount',
    'EAA': 'Early buy allowance - Discount for ordering early',
    'EAB': 'Early payment allowance - Discount for early payment',
    'FC': 'Freight charge - Charge for transporting goods',
    'FI': 'Finance charge - Charge for financial services',
    'HD': 'Handling - Charge for the handling of goods',
    'PAD': 'Pallet charge - Charge for pallet use',
    'QD': 'Quantity discount - Discount based on quantity purchased',
    'RAA': 'Rebate',
    'SAB': 'Special credit agreement',
    'TAE': 'Transport emergency',
    'TD': 'Trade discount',
    'TX': 'Tax'
  },
  
  // Tax category codes (used in TAX segment)
  'taxCategoryCodes': {
    'A': 'Mixed tax rate - More than one tax rate applies',
    'E': 'Exempt from tax - No tax is charged',
    'S': 'Standard rate - The standard rate of tax applies',
    'Z': 'Zero rated goods - Tax rate is zero'
  },
  
  // Transport mode codes (used in TDT segment)
  'transportModeCodes': {
    '1': 'Maritime transport - Transport of goods by sea',
    '2': 'Rail transport - Transport of goods by rail',
    '3': 'Road transport - Transport of goods by road',
    '4': 'Air transport - Transport of goods by air',
    '8': 'Inland water transport - Transport of goods by inland waterway',
    '9': 'Mode not specified - Transport mode is not specified'
  },
  
  // Message function codes (used in BGM segment)
  'messageFunctionCodes': {
    '1': 'Cancellation - Document/message canceling a previous transmission',
    '2': 'Addition - Document/message adding information to a previous transmission',
    '3': 'Deletion - Document/message deleting a previous transmission',
    '4': 'Change - Document/message changing a previous transmission',
    '5': 'Replace - Document/message replacing a previous transmission',
    '6': 'Confirmation - Document/message confirming details of a previous transmission',
    '7': 'Duplicate - Duplicate of a document/message previously sent',
    '8': 'Status - Document/message indicating the status of a previous transmission',
    '9': 'Original - Initial transmission of a document/message',
    '10': 'Not found - Document/message indicating that the referenced document was not found',
    '11': 'Response - Document/message responding to a previous message or document',
    '12': 'Not processed - Document/message indicating that a previous transmission was not processed',
    '13': 'Request - Document/message requesting some action or information',
    '14': 'Advance notification - Document/message providing advance notification of information',
    '15': 'Reminder - Document/message reminding of something requiring attention',
    '16': 'Proposal - Document/message containing a proposal',
    '17': 'Cancel, to be reissued - Document/message that will be reissued in a corrected form',
    '27': 'Not accepted - Document/message not accepted by the recipient',
    '29': 'Accepted without amendment - Document/message accepted without amendment',
    '31': 'Copy - Copy of original document',
    '34': 'Accepted with amendment - Document/message accepted with amendment',
    '42': 'Confirmation via specific means - Confirmation sent via a specific means',
    '44': 'Accepted without reserves - Document/message accepted without reservation',
    '45': 'Accepted with reserves - Document/message accepted with reservation',
    '46': 'Provisional - Document/message content is provisional',
    '47': 'Definitive - Document/message content is definitive',
    '53': 'Test - Document/message intended for testing purposes',
    '55': 'Notification only - Document/message is for notification only'
  },
  
  // Response type codes (used in BGM segment)
  'responseTypeCodes': {
    'AC': 'Acknowledge - With detail and change - Acknowledge message including the ability to detail changes',
    'AA': 'Acknowledge - With detail no change - Acknowledge message including the ability to detail without changes',
    'AB': 'Acknowledge - Only - Acknowledge message only',
    'AI': 'Acknowledge only if an exception condition exists - Only acknowledge if there are exceptions',
    'NA': 'No acknowledgement needed - No acknowledgement required',
    '1': 'Required - A response is required',
    '2': 'Not required - A response is not required',
    '3': 'Preferred - Response is preferred but not mandatory'
  },
  
  // Document/message name codes (used in BGM segment)
  'documentCodeQualifiers': {
    '9': 'Purchase order - Document/message by means of which a buyer initiates a transaction with a seller',
    '105': 'Purchase order change request - Change to an existing purchase order',
    '220': 'Order - Document/message by means of which a buyer initiates a transaction with a seller',
    '231': 'Purchase order response - Response to an order',
    '240': 'Delivery instruction - Document/message issued by a buyer giving instructions regarding the details of the delivery of goods ordered',
    '241': 'Delivery order - Document/message issued by a party entitled to authorize delivery of goods',
    '242': 'Delivery state - Document/message specifying the actual delivery state of goods',
    '244': 'Delivery release - Document/message issued by a buyer releasing a delivery from the hold status',
    '310': 'Offer/quotation - Document/message which, with a view to concluding a contract, sets out price and terms under which goods are offered',
    '351': 'Dispatch advice - Document/message by means of which the seller or consignor informs the consignee about the dispatch of goods',
    '380': 'Commercial invoice - Document/message claiming payment for goods or services supplied',
    '481': 'Remittance advice - Document/message advising of funds transfer'
  },
  
  // Tax function qualifier codes (used in TAX segment)
  'taxFunctionQualifiers': {
    '1': 'Customs duty - Tax levied on imports by customs authorities',
    '2': 'Tax - Any type of tax not specified elsewhere',
    '3': 'Statistical fee - Fee charged for statistical purposes',
    '4': 'Mixed tax rate - Combination of different taxes',
    '5': 'Duty/tax/fee paid by supplier - Charges paid by the selling party',
    '6': 'Duty/tax/fee paid by buyer - Charges paid by the purchasing party',
    '7': 'VAT - Value Added Tax charged on goods and services',
    '8': 'Sales tax - state/provincial - Tax levied at state or provincial level',
    '9': 'Sales tax - national - Tax levied at national level',
    '10': 'Value added tax (VAT) - Synonym for code 7',
    '11': 'Goods and services tax - Tax on goods and services',
    '12': 'Excise duty - Tax on specific products like alcohol or tobacco',
    '13': 'Construction tax - Tax specific to construction services',
    '14': 'Energy fee - Tax or fee related to energy consumption',
    '15': 'Warranty related costs - Costs associated with warranty'
  },
  
  // Contact function qualifiers (used in CTA segment)
  'contactFunctionQualifiers': {
    'AD': 'Accounting - Contact for accounting matters',
    'AP': 'Accounts payable - Contact for accounts payable',
    'AR': 'Accounts receivable - Contact for accounts receivable',
    'CA': 'Carrier - Contact for shipping arrangements',
    'CT': 'Contract contact - Contact for contract matters',
    'DL': 'Delivery contact - Contact for delivery information',
    'ED': 'Engineering department - Contact for engineering issues',
    'IC': 'Information contact - Contact for information',
    'LO': 'Logistics contact - Contact for logistics matters',
    'OC': 'Order contact - Contact for ordering',
    'QC': 'Quality coordinator - Contact for quality issues',
    'SA': 'Sales administration - Contact for sales administration',
    'SD': 'Shipping department - Contact for shipping matters',
    'TR': 'Transport contact - Contact for transport arrangements',
    'PD': 'Purchasing department - Contact for purchasing matters'
  },
  
  // Currency qualifiers (used in CUX segment)
  'currencyQualifiers': {
    '2': 'Reference currency - The currency applicable to amounts stated',
    '3': 'Target currency - The currency to which a currency amount is to be converted',
    '5': 'Accounting currency - The currency in which accounts are kept',
    '9': 'Order currency - The currency in which an order is valued',
    '10': 'Pricing currency - The currency in which a price is expressed',
    '11': 'Payment currency - The currency in which a payment is made'
  },
  
  // Product ID function qualifiers (used in PIA segment)
  'productIDQualifiers': {
    '1': 'Additional identification - Additional identification to the one specified in the LIN segment',
    '2': 'Product identification - Commodity/product identification number',
    '3': 'Supplier\'s identification - Number assigned by supplier',
    '4': 'Shipment identification - Number assigned for shipment purposes',
    '5': 'Product group - Code identifying a product group'
  },
  
  // Item description type qualifiers (used in IMD segment)
  'itemDescriptionTypeQualifiers': {
    'A': 'Added description - Description added to the specification of an item',
    'C': 'Changed description - Description replacing the existing one',
    'D': 'Deleted description - Description to be deleted',
    'F': 'Free-form description - Description in free form text'
  },
  
  // Item characteristic codes (used in IMD segment)
  'itemCharacteristicCodes': {
    'AC': 'Additional identification - Additional information used for identification',
    'BS': 'Brand name - Name used to identify a brand',
    'IN': 'Insurance - Details regarding insurance',
    'MD': 'Method of delivery - Method by which the goods will be delivered',
    'MN': 'Model number - Reference number assigned to a specific model',
    'PC': 'Product code - Code assigned to a product',
    'PO': 'Process - Process carried out on the product',
    'SN': 'Serial number - Unique number assigned to a product',
    'TF': 'Technical features - Features of a technical nature'
  },
  
  // Communication type qualifiers (used in COM segment)
  'communicationTypeQualifiers': {
    'EM': 'Electronic mail - Communication via electronic mail',
    'FX': 'Fax - Communication via facsimile',
    'TE': 'Telephone - Communication via telephone',
    'TL': 'Telex - Communication via telex',
    'XF': 'X.400 - Communication via X.400 electronic mail',
    'IC': 'Information contact number - Number to contact for information',
    'TG': 'Telegraph - Communication via telegraph',
    'AO': 'Uniform Resource Location (URL) - Communication via internet URL',
    'EI': 'EDI transmission - Communication via EDI',
    'MA': 'Mail - Communication via postal service',
    'PL': 'Mobile phone - Communication via mobile phone'
  },
  
  // Control total qualifiers (used in CNT segment)
  'controlTotalQualifiers': {
    '1': 'Total number of line items - Count of the number of line items in the message',
    '2': 'Total number of messages - Count of the number of messages',
    '3': 'Control count - Count for control purposes',
    '11': 'Total number of packages - Count of the number of packages',
    '15': 'Total consignment quantity - Count of the total consignment',
    '16': 'Total number of sequence details - Count of the number of sequence details',
    '1E': 'Number of units - Count of the number of units',
    '2E': 'Number of segments - Count of the number of segments'
  },
  
  // Section identification qualifiers (used in UNS segment)
  'sectionIdentificationQualifiers': {
    'D': 'Detail section - Detail section of the message',
    'H': 'Header section - Header section of the message',
    'S': 'Summary section - Summary section of the message'
  },
  
  // Financial Institution qualifiers (used in FII segment)
  'financialInstitutionQualifiers': {
    'BF': 'Beneficiary\'s bank - Financial institution where the beneficiary maintains an account',
    'DP': 'Receiving financial institution - Financial institution which will receive the payment',
    'RH': 'Clearing house - Central processing location for financial transactions',
    'PB': 'Ordered bank - Financial institution from where payment is ordered',
    'PE': 'Payee - Party to whom payment is made',
    'PR': 'Payer - Party who initiates payment',
    'RB': 'Receiving bank - Financial institution which receives the payment',
    'BB': 'Issuing bank - Financial institution issuing the payment instrument'
  },
  
  // Syntax identifier qualifiers (used in UNB segment)
  'syntaxIdentifierQualifiers': {
    'UNOA': 'UN/ECE level A - Character repertoire level A (basic ASCII set)',
    'UNOB': 'UN/ECE level B - Character repertoire level B (extended ASCII set)',
    'UNOC': 'UN/ECE level C - Character repertoire level C (extended special characters)',
    'UNOD': 'UN/ECE level D - Character repertoire level D (Latin alphabet No. 5)',
    'UNOE': 'UN/ECE level E - Character repertoire level E (Latin alphabet No. 1)',
    'UNOF': 'UN/ECE level F - Character repertoire level F (extended Latin alphabet)'
  },
  
  // Identification code qualifiers (used in UNB sender/recipient identifiers)
  'identificationCodeQualifiers': {
    '1': 'DUNS (Data Universal Numbering System) - Dun & Bradstreet coding to identify companies',
    '5': 'ILN (International Location Number) - Deprecated term for GLN', 
    '9': 'SWIFT address - Identifier used in banking/financial transactions',
    '14': 'EAN/GS1 - Global Location Number (GLN) - International standard for location identification',
    '16': 'EDIFACT/UN - United Nations standard identifier',
    '30': 'ISO 6523 - Organization identification according to ISO 6523 standard',
    '33': 'BIC (Bank Identifier Code) - International bank identifier according to ISO 9362',
    '84': 'IATA - International Air Transport Association identifier',
    '85': 'ISIC - International Standard Industrial Classification identifier',
    '100': 'National identifier - Identifier assigned by a national authority',
    '160': 'Party tax number - Tax identification number of the party',
    '192': 'GTIN - Global Trade Item Number used to identify trade items',
    'ZZZ': 'Mutually defined - Identifier agreed upon between interchange partners'
  },
  
  // Message type qualifiers (used in UNH segment)
  'messageTypeQualifiers': {
    'INVOIC': 'Invoice - Document/message claiming payment for goods or services supplied',
    'ORDERS': 'Purchase order - Document/message requesting the supply of goods or services',
    'ORDRSP': 'Purchase order response - Response to an order message',
    'DESADV': 'Despatch advice - Document/message providing shipping details and package contents',
    'RECADV': 'Receiving advice - Document/message providing receiving details',
    'QUOTES': 'Quote - Document/message offering goods or services',
    'REMADV': 'Remittance advice - Document/message providing remittance information',
    'REQOTE': 'Request for quote - Document/message requesting a quote',
    'PRICAT': 'Price/sales catalogue - Document/message containing price and sales catalog information',
    'SLSRPT': 'Sales report - Document/message reporting sales data',
    'INVRPT': 'Inventory report - Document/message reporting inventory information',
    'PARTIN': 'Party information - Document/message providing party information',
    'PRODAT': 'Product data - Document/message providing product specification data',
    'IFTMIN': 'Instruction for transport - Document/message containing transport instructions',
    'IFTSTA': 'Transport status - Document/message providing transport status',
    'COARRI': 'Container arrival - Document/message reporting container arrival',
    'CONTRL': 'Control - Document/message acknowledging receipt of another message'
  },
  
  // Version qualifiers (used in UNH segment)
  'versionQualifiers': {
    'D': 'Draft directory - Pre-release version under development',
    'S': 'Standard directory - Official standard release'
  },
  
  // Release qualifiers (used in UNH segment)
  'releaseQualifiers': {
    '93A': 'Release 1993-A',
    '96A': 'Release 1996-A',
    '96B': 'Release 1996-B',
    '97A': 'Release 1997-A',
    '01B': 'Release 2001-B',
    '01C': 'Release 2001-C',
    '02A': 'Release 2002-A',
    '03A': 'Release 2003-A',
    '03B': 'Release 2003-B',
    '04A': 'Release 2004-A',
    '04B': 'Release 2004-B',
    '05A': 'Release 2005-A',
    '05B': 'Release 2005-B',
    '06A': 'Release 2006-A',
    '06B': 'Release 2006-B',
    '07A': 'Release 2007-A',
    '07B': 'Release 2007-B',
    '08A': 'Release 2008-A',
    '08B': 'Release 2008-B',
    '09A': 'Release 2009-A',
    '09B': 'Release 2009-B',
    '10A': 'Release 2010-A',
    '10B': 'Release 2010-B',
    '11A': 'Release 2011-A',
    '11B': 'Release 2011-B'
  },
  
  // Controlling agency qualifiers (used in UNH segment)
  'controllingAgencyQualifiers': {
    'UN': 'United Nations Economic Commission for Europe (UN/ECE)',
    'GS1': 'GS1 international organization',
    'EAN': 'European Article Numbering (now GS1)',
    'UCC': 'Uniform Code Council (now GS1 US)',
    'IATA': 'International Air Transport Association',
    'CEN': 'European Committee for Standardization',
    'ANSI': 'American National Standards Institute',
    'ISO': 'International Organization for Standardization'
  }
};

/**
 * Gets a description for a segment type
 * @param {string} segmentType - The segment type code 
 * @returns {string} Description of the segment
 */
const getSegmentDescription = (segmentType) => {
  return segmentDescriptions[segmentType] || `${segmentType} segment`;
};

/**
 * Gets detailed information about a segment
 * @param {string} segmentType - The segment type code 
 * @returns {object|null} Detailed information about the segment
 */
const getSegmentDetailedInfo = (segmentType) => {
  return segmentExtendedInfo[segmentType] || null;
};

/**
 * Gets information about specific elements or qualifiers within a segment
 * @param {string} segmentType - The segment type code
 * @param {number} elementPosition - The position of the element within the segment
 * @returns {object|null} Information about the element or null if not found
 */
const getElementDescription = (segmentType, elementPosition) => {
  // Special handling for different segment types
  const segmentInfo = segmentExtendedInfo[segmentType];
  
  if (!segmentInfo) {
    return null;
  }
  
  // Special handling for BGM first element which might be a direct value or a composite
  if (segmentType === 'BGM' && elementPosition === 1) {
    console.log("BGM Element 1 detected, providing explanations:", 
      "Values:", segmentInfo.qualifiers, 
      "Explanations:", qualifierCodesExplanations.documentCodeQualifiers);
    return {
      name: 'Document/Message Name, Coded',
      description: 'Code identifying the document/message',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.documentCodeQualifiers
    };
  }
  
  // For segments with qualifier lists
  if (segmentType === 'DTM' && elementPosition === 1) {
    return {
      name: 'Date/Time/Period Qualifier',
      description: 'Qualifier identifying the function of a date/time/period',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.dateTimeQualifiers
    };
  } else if (segmentType === 'DTM' && elementPosition === 2) {
    return {
      name: 'Format Qualifier',
      description: 'Qualifier identifying the format of the date/time/period',
      values: segmentInfo.formats
    };
  } else if (segmentType === 'NAD' && elementPosition === 1) {
    return {
      name: 'Party Qualifier',
      description: 'Qualifier identifying the party',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.partyQualifiers
    };
  } else if (segmentType === 'QTY' && elementPosition === 1) {
    return {
      name: 'Quantity Qualifier',
      description: 'Qualifier identifying the function of the quantity',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.quantityQualifiers
    };
  } else if (segmentType === 'MOA' && elementPosition === 1) {
    return {
      name: 'Monetary Amount Qualifier',
      description: 'Qualifier identifying the function of the monetary amount',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.monetaryAmountQualifiers
    };
  } else if (segmentType === 'PRI' && elementPosition === 1) {
    return {
      name: 'Price Qualifier',
      description: 'Qualifier identifying the function of the price',
      values: segmentInfo.qualifiers
    };
  } else if (segmentType === 'RFF' && elementPosition === 1) {
    return {
      name: 'Reference Qualifier',
      description: 'Qualifier identifying the reference',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.referenceQualifiers
    };
  } else if (segmentType === 'LOC' && elementPosition === 1) {
    return {
      name: 'Location Qualifier',
      description: 'Qualifier identifying the function of the location',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.locationQualifiers
    };
  } else if (segmentType === 'ALC' && elementPosition === 1) {
    return {
      name: 'Allowance or Charge Qualifier',
      description: 'Qualifier indicating whether this is an allowance or a charge',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.allowanceChargeQualifiers
    };
  } else if ((segmentType === 'PAT' || segmentType === 'PYT') && elementPosition === 1) {
    return {
      name: 'Payment Terms Type Qualifier',
      description: 'Qualifier identifying the type of payment terms',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.paymentTermsQualifiers
    };
  } else if (segmentType === 'BGM' && elementPosition === 2) {
    return {
      name: 'Document/Message Identification',
      description: 'Document number/identifier'
    };
  } else if (segmentType === 'BGM' && elementPosition === 3) {
    return {
      name: 'Message Function Code',
      description: 'Code indicating the function of the message',
      values: segmentInfo.functionCodes,
      explanations: qualifierCodesExplanations.messageFunctionCodes
    };
  } else if (segmentType === 'BGM' && elementPosition === 4) {
    return {
      name: 'Response Type Code',
      description: 'Code indicating the type of response required',
      values: segmentInfo.responseTypeCodes,
      explanations: qualifierCodesExplanations.responseTypeCodes
    };
  } else if (segmentType === 'TDT' && elementPosition === 3) {
    return {
      name: 'Mode of Transport',
      description: 'Code specifying the mode of transport',
      values: segmentInfo.transportModes,
      explanations: qualifierCodesExplanations.transportModeCodes
    };
  } else if (segmentType === 'TAX' && elementPosition === 1) {
    return {
      name: 'Duty/Tax/Fee Function Qualifier',
      description: 'Qualifier identifying the function of the duty/tax/fee',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.taxFunctionQualifiers
    };
  } else if (segmentType === 'TAX' && elementPosition === 6) {
    return {
      name: 'Duty/Tax/Fee Category, Coded',
      description: 'Code identifying the category of duty, tax or fee',
      values: segmentInfo.taxCategories,
      explanations: qualifierCodesExplanations.taxCategoryCodes
    };
  } else if (segmentType === 'CTA' && elementPosition === 1) {
    return {
      name: 'Contact Function, Coded',
      description: 'Code identifying the function of the contact',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.contactFunctionQualifiers
    };
  } else if (segmentType === 'CUX' && elementPosition === 1) {
    return {
      name: 'Currency Details',
      description: 'Currency qualifier and code',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.currencyQualifiers
    };
  } else if (segmentType === 'PIA' && elementPosition === 1) {
    return {
      name: 'Product ID Function Qualifier',
      description: 'Code giving specific meaning to the product ID',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.productIDQualifiers
    };
  } else if (segmentType === 'IMD' && elementPosition === 1) {
    return {
      name: 'Item Description Type, Coded',
      description: 'Code indicating the format of the description',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.itemDescriptionTypeQualifiers
    };
  } else if (segmentType === 'IMD' && elementPosition === 2) {
    return {
      name: 'Item Characteristic, Coded',
      description: 'Code specifying the characteristic of the item',
      values: segmentInfo.characteristicCodes,
      explanations: qualifierCodesExplanations.itemCharacteristicCodes
    };
  } else if (segmentType === 'COM' && elementPosition === 1) {
    return {
      name: 'Communication Contact',
      description: 'Communication number with qualifier',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.communicationTypeQualifiers
    };
  } else if (segmentType === 'CNT' && elementPosition === 1) {
    return {
      name: 'Control',
      description: 'Control totals with qualifier',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.controlTotalQualifiers
    };
  } else if (segmentType === 'UNS' && elementPosition === 1) {
    return {
      name: 'Section Identification',
      description: 'Code to identify the section of the message',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.sectionIdentificationQualifiers
    };
  } else if (segmentType === 'FII' && elementPosition === 1) {
    return {
      name: 'Party Qualifier',
      description: 'Code identifying the function of the financial institution party',
      values: segmentInfo.qualifiers,
      explanations: qualifierCodesExplanations.financialInstitutionQualifiers
    };
  } else if (segmentType === 'UNB' && elementPosition === 1) {
    return {
      name: 'Syntax Identifier',
      description: 'Identifies the syntax rules and character set used in the interchange',
      explanations: qualifierCodesExplanations.syntaxIdentifierQualifiers
    };
  } else if ((segmentType === 'UNB' && (elementPosition === 2 || elementPosition === 3)) || 
             (segmentType === 'NAD' && elementPosition === 2)) {
    // Handle UNB sender/recipient identification code qualifiers or NAD identification
    return {
      name: elementPosition === 2 ? 'Sender Identification' : 'Recipient Identification',
      description: elementPosition === 2 ? 'Identification of the sender of the interchange' : 'Identification of the recipient of the interchange',
      explanations: qualifierCodesExplanations.identificationCodeQualifiers
    };
  } else if (segmentType === 'UNH' && elementPosition === 2) {
    // Handle UNH message identifier composite element
    return {
      name: 'Message Identifier',
      description: 'Identification of the message type, version, release, and controlling agency',
      subElements: [
        { 
          name: 'Message type', 
          description: 'Type of message (e.g., ORDERS, INVOIC, DESADV)',
          explanations: qualifierCodesExplanations.messageTypeQualifiers
        },
        {
          name: 'Message version number',
          description: 'Version number of the message type',
          explanations: qualifierCodesExplanations.versionQualifiers
        },
        {
          name: 'Message release number',
          description: 'Release number within the message version',
          explanations: qualifierCodesExplanations.releaseQualifiers
        },
        {
          name: 'Controlling agency',
          description: 'Agency controlling the message specification',
          explanations: qualifierCodesExplanations.controllingAgencyQualifiers
        }
      ]
    };
  }
  
  // For elements with defined descriptions
  if (segmentInfo.elements && elementPosition <= segmentInfo.elements.length) {
    const element = segmentInfo.elements[elementPosition - 1];
    return {
      name: element.name,
      description: element.description,
      mandatory: element.mandatory,
      examples: element.examples,
      subElements: element.subElements
    };
  }
  
  return null;
};

/**
 * Gets the meaning of a specific code value for a given code type
 * @param {string} codeType - The type of code (e.g., 'packageTypes', 'transportModes')
 * @param {string} codeValue - The code value to look up
 * @returns {string|null} The meaning of the code value, or null if not found
 */
const getCodeValueMeaning = (codeType, codeValue) => {
  if (codeValueMeanings[codeType] && codeValueMeanings[codeType][codeValue]) {
    return codeValueMeanings[codeType][codeValue];
  }
  return null;
};

/**
 * Gets the explanation for a qualifier code from a specific qualifier type
 * @param {string} qualifierType - The type of qualifier (e.g., 'partyQualifiers', 'dateTimeQualifiers')
 * @param {string} qualifierCode - The qualifier code to look up
 * @returns {string|null} The explanation of the qualifier code, or null if not found
 */
const getQualifierCodeExplanation = (qualifierType, qualifierCode) => {
  if (qualifierCodesExplanations[qualifierType] && qualifierCodesExplanations[qualifierType][qualifierCode]) {
    return qualifierCodesExplanations[qualifierType][qualifierCode];
  }
  return null;
};

export { 
  getSegmentDescription, 
  getSegmentDetailedInfo,
  getElementDescription,
  getCodeValueMeaning,
  codeValueMeanings,
  qualifierCodesExplanations,
  getQualifierCodeExplanation
}; 